import React, { useRef, useEffect, useCallback, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import useStore from '../store/useStore';
import { Loader2 } from 'lucide-react';
import { isPamyatniki } from '../lib/variant';

// LoadingManager для отслеживания загрузки начальных текстур
const _textureLoadingManager = new THREE.LoadingManager();
let _initialTexturesLoaded = false;
const _textureReadyCallbacks = [];
_textureLoadingManager.onLoad = () => {
  _initialTexturesLoaded = true;
  _textureReadyCallbacks.forEach(cb => cb());
  _textureReadyCallbacks.length = 0;
};

// Текстуры загружаются один раз при загрузке модуля, передаются в material.map при создании мешей (без колбэков/эффектов)
const textureLoader = new THREE.TextureLoader(_textureLoadingManager);
// Памятники стоят на ухоженном газоне, благоустройство — на сухом грунте (бывшая
// текстура). Терсер свернёт тернар на этапе сборки (isPamyatniki = const), для
// blago бандл остаётся байт-идентичным.
const groundTexture = textureLoader.load(isPamyatniki ? '/textures/grass.jpg' : '/textures/ground.jpg');
groundTexture.wrapS = groundTexture.wrapT = THREE.RepeatWrapping;
groundTexture.repeat.set(isPamyatniki ? 8 : 6, isPamyatniki ? 8 : 6);
if (groundTexture.colorSpace !== undefined) groundTexture.colorSpace = THREE.SRGBColorSpace;

const grassTexture = textureLoader.load('/textures/grass.jpg');
grassTexture.wrapS = grassTexture.wrapT = THREE.RepeatWrapping;
if (grassTexture.colorSpace !== undefined) grassTexture.colorSpace = THREE.SRGBColorSpace;

// Тяжёлые текстуры материалов (гранит/мрамор/бетон/земля) для pamyatniki грузим
// ВНЕ LoadingManager — отдельным загрузчиком, чтобы оверлей «Загрузка текстур» не ждал
// их и сцена показывалась сразу (оверлей ждёт только газон). Текстуры подтягиваются
// прогрессивно по мере готовности. Для blago тернар сворачивается Терсером в
// textureLoader (managed) — поведение и бандл байт-идентичны прежним.
const flowerbedInteriorTexture = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/zemlya.jpg');
flowerbedInteriorTexture.wrapS = flowerbedInteriorTexture.wrapT = THREE.RepeatWrapping;
if (flowerbedInteriorTexture.colorSpace !== undefined) flowerbedInteriorTexture.colorSpace = THREE.SRGBColorSpace;

// Текстура земли для фундамента "Земля" — используем ту же картинку, но с отдельным тайлингом по размеру участка
const earthFoundationTexture = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/zemlya.jpg');
earthFoundationTexture.wrapS = earthFoundationTexture.wrapT = THREE.RepeatWrapping;
if (earthFoundationTexture.colorSpace !== undefined) earthFoundationTexture.colorSpace = THREE.SRGBColorSpace;

// Текстуры гранита для памятника — тайлинг + анизотропная фильтрация для чёткости под углом
const redGraniteTexture = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/красный-гранит.jpg');
redGraniteTexture.wrapS = redGraniteTexture.wrapT = THREE.RepeatWrapping;
redGraniteTexture.repeat.set(1, 2);
redGraniteTexture.anisotropy = 16;
if (redGraniteTexture.colorSpace !== undefined) redGraniteTexture.colorSpace = THREE.SRGBColorSpace;

const kurtyGraniteTexture = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/курты.webp');
kurtyGraniteTexture.wrapS = kurtyGraniteTexture.wrapT = THREE.RepeatWrapping;
kurtyGraniteTexture.repeat.set(1, 2);
kurtyGraniteTexture.anisotropy = 16;
if (kurtyGraniteTexture.colorSpace !== undefined) kurtyGraniteTexture.colorSpace = THREE.SRGBColorSpace;

const blackGraniteTexture = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/черный_гранит.jpeg');
blackGraniteTexture.wrapS = blackGraniteTexture.wrapT = THREE.RepeatWrapping;
blackGraniteTexture.repeat.set(1, 2);
blackGraniteTexture.anisotropy = 16;
if (blackGraniteTexture.colorSpace !== undefined) blackGraniteTexture.colorSpace = THREE.SRGBColorSpace;

const greenGraniteTexture = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/зеленый_гранит.jpg');
greenGraniteTexture.wrapS = greenGraniteTexture.wrapT = THREE.RepeatWrapping;
greenGraniteTexture.repeat.set(1, 2);
greenGraniteTexture.anisotropy = 16;
if (greenGraniteTexture.colorSpace !== undefined) greenGraniteTexture.colorSpace = THREE.SRGBColorSpace;

const greyGraniteTexture = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/grey_granit.jpg');
greyGraniteTexture.wrapS = greyGraniteTexture.wrapT = THREE.RepeatWrapping;
greyGraniteTexture.repeat.set(1, 2);
greyGraniteTexture.anisotropy = 16;
if (greyGraniteTexture.colorSpace !== undefined) greyGraniteTexture.colorSpace = THREE.SRGBColorSpace;

const concreteTexture = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/бетон.jpg');
concreteTexture.wrapS = concreteTexture.wrapT = THREE.RepeatWrapping;
concreteTexture.repeat.set(2, 2);
concreteTexture.anisotropy = 16;
if (concreteTexture.colorSpace !== undefined) concreteTexture.colorSpace = THREE.SRGBColorSpace;

const marbleTexture = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/mramor.jpg');
marbleTexture.wrapS = marbleTexture.wrapT = THREE.RepeatWrapping;
if (marbleTexture.colorSpace !== undefined) marbleTexture.colorSpace = THREE.SRGBColorSpace;

// Отдельный экземпляр мрамора для внутренности цветника — крупнее (меньше повторений)
const marbleTextureFlowerbed = (isPamyatniki ? new THREE.TextureLoader() : textureLoader).load('/textures/mramor.jpg');
marbleTextureFlowerbed.wrapS = marbleTextureFlowerbed.wrapT = THREE.RepeatWrapping;
if (marbleTextureFlowerbed.colorSpace !== undefined) marbleTextureFlowerbed.colorSpace = THREE.SRGBColorSpace;

// Удалённые/внешние URL текстур (например Supabase) — грузим по ссылке и кэшируем
const remoteTextureLoader = new THREE.TextureLoader();
// Важно для загрузки картинок с Supabase (CORS)
remoteTextureLoader.crossOrigin = 'anonymous';
const remoteTextureCache = new Map(); // url -> THREE.Texture
// Портрет гравировки — отдельный кэш и настройки (нельзя смешивать с полом)
const engravingPortraitTextureCache = new Map();

// ---- Engraving canvas helpers ----

function drawPortraitSilhouette(canvas) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width;
  const H = canvas.height;
  // Fully transparent background — monument granite shows through
  ctx.clearRect(0, 0, W, H);

  const sil = 'rgba(255,255,255,0.95)';

  // === HEAD ===
  const hcx = W / 2;
  const hcy = H * 0.22;
  const hrx = W * 0.16;
  const hry = H * 0.155;
  ctx.fillStyle = sil;
  ctx.beginPath();
  ctx.ellipse(hcx, hcy, hrx, hry, 0, 0, Math.PI * 2);
  ctx.fill();

  // === NECK ===
  const nw = W * 0.075;
  const ntop = hcy + hry * 0.78;
  const nbot = hcy + hry * 1.48;
  ctx.fillStyle = sil;
  ctx.fillRect(W / 2 - nw, ntop, nw * 2, nbot - ntop);

  // === BODY + SHOULDERS ===
  const sw = W * 0.46;
  ctx.fillStyle = sil;
  ctx.beginPath();
  ctx.moveTo(W / 2 - nw, nbot);
  ctx.bezierCurveTo(
    W / 2 - nw * 2.2, nbot + H * 0.005,
    W / 2 - sw * 0.8,  nbot + H * 0.055,
    W / 2 - sw,        nbot + H * 0.12
  );
  ctx.lineTo(W / 2 - sw, H);
  ctx.lineTo(W / 2 + sw, H);
  ctx.lineTo(W / 2 + sw, nbot + H * 0.12);
  ctx.bezierCurveTo(
    W / 2 + sw * 0.8,  nbot + H * 0.055,
    W / 2 + nw * 2.2, nbot + H * 0.005,
    W / 2 + nw,        nbot
  );
  ctx.closePath();
  ctx.fill();

  // === LAPEL CUTOUTS (suit collar V) — use composite op ===
  ctx.globalCompositeOperation = 'destination-out';
  // Left lapel
  ctx.beginPath();
  ctx.moveTo(W / 2 - nw * 0.9, nbot);
  ctx.lineTo(W / 2 - sw * 0.44, nbot + H * 0.13);
  ctx.lineTo(W / 2,             H * 0.62);
  ctx.lineTo(W / 2,             nbot + H * 0.005);
  ctx.closePath();
  ctx.fill();
  // Right lapel
  ctx.beginPath();
  ctx.moveTo(W / 2 + nw * 0.9, nbot);
  ctx.lineTo(W / 2 + sw * 0.44, nbot + H * 0.13);
  ctx.lineTo(W / 2,             H * 0.62);
  ctx.lineTo(W / 2,             nbot + H * 0.005);
  ctx.closePath();
  ctx.fill();
  ctx.globalCompositeOperation = 'source-over';

  // === TIE ===
  ctx.fillStyle = 'rgba(220,220,220,0.9)';
  ctx.beginPath();
  ctx.moveTo(W / 2 - nw * 0.5, nbot + H * 0.006);
  ctx.lineTo(W / 2 + nw * 0.5, nbot + H * 0.006);
  ctx.lineTo(W / 2 + nw * 0.28, nbot + H * 0.068);
  ctx.lineTo(W / 2,             H * 0.74);
  ctx.lineTo(W / 2 - nw * 0.28, nbot + H * 0.068);
  ctx.closePath();
  ctx.fill();

  // Tie knot
  ctx.fillStyle = 'rgba(240,240,240,0.95)';
  ctx.beginPath();
  ctx.ellipse(W / 2, nbot + H * 0.006, nw * 0.42, H * 0.016, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawFIOCanvas(canvas, element) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width;
  const H = canvas.height;
  // Transparent background — monument granite shows through
  ctx.clearRect(0, 0, W, H);

  const lastName  = element.lastName  || 'Фамилия';
  const firstName = element.firstName || 'Имя';
  const patronymic = element.patronymic || 'Отчество';

  const scale = W / 512;
  const lastNamePx  = Math.round((element.lastNameSize  || 32) * scale * 4.0);
  const firstNamePx = Math.round((element.firstNameSize || 26) * scale * 4.0);
  const patronymicPx = Math.round((element.patronymicSize || 26) * scale * 4.0);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // White colour for readability on granite
  const textColor = 'rgba(255,255,255,0.97)';

  const step = H / 4;

  // Shadow for readability on any background
  ctx.shadowColor = 'rgba(0,0,0,0.85)';
  ctx.shadowBlur = Math.max(4, W * 0.02);
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = Math.max(2, W * 0.005);

  ctx.fillStyle = textColor;

  // Helper: fit font size so text never overflows canvas width (with 5% padding each side)
  const maxW = W * 0.90;
  const fitFont = (baseFont, text) => {
    ctx.font = baseFont;
    const measured = ctx.measureText(text).width;
    if (measured <= maxW) return baseFont;
    // Scale down proportionally
    const match = baseFont.match(/(\d+)px/);
    if (!match) return baseFont;
    const fitted = Math.floor(parseInt(match[1]) * maxW / measured);
    return baseFont.replace(/\d+px/, fitted + 'px');
  };

  // Last name — bold italic
  ctx.font = fitFont(`bold italic ${lastNamePx}px Georgia, "Times New Roman", serif`, lastName);
  ctx.fillText(lastName, W / 2, step);

  // First name — italic
  ctx.font = fitFont(`italic ${firstNamePx}px Georgia, "Times New Roman", serif`, firstName);
  ctx.fillText(firstName, W / 2, step * 2);

  // Patronymic — italic
  ctx.font = fitFont(`italic ${patronymicPx}px Georgia, "Times New Roman", serif`, patronymic);
  ctx.fillText(patronymic, W / 2, step * 3);

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
}

// Эпитафия: одна курсивная строка свободного текста (внизу грани, под ФИО).
// Пусто → показываем образец «Текст эпитафии».
function drawEpitaphCanvas(canvas, element) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  ctx.clearRect(0, 0, W, H);
  const text = (element.epitaphText && element.epitaphText.trim()) || 'Текст эпитафии';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(255,255,255,0.97)';
  ctx.shadowColor = 'rgba(0,0,0,0.85)';
  ctx.shadowBlur = Math.max(4, W * 0.02);
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = Math.max(2, W * 0.005);
  const maxW = W * 0.92;
  let px = Math.round(H * 0.6);
  ctx.font = `italic ${px}px Georgia, "Times New Roman", serif`;
  const measured = ctx.measureText(text).width;
  if (measured > maxW) {
    px = Math.floor(px * maxW / measured);
    ctx.font = `italic ${px}px Georgia, "Times New Roman", serif`;
  }
  ctx.fillText(text, W / 2, H / 2);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
}

function getRemoteTexture(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  if (remoteTextureCache.has(u)) return remoteTextureCache.get(u);

  // Создаём текстуру сразу; изображение подгрузится асинхронно и Three обновит рендер
  const tex = remoteTextureLoader.load(
    u,
    () => {
      tex.needsUpdate = true;
    },
    undefined,
    (err) => {
      // Если не загрузилось — оставим fallback на цвет
      console.warn('Floor texture failed to load:', u, err);
    }
  );
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 16;
  if (tex.colorSpace !== undefined) tex.colorSpace = THREE.SRGBColorSpace;
  remoteTextureCache.set(u, tex);
  return tex;
}

// Текстура SVG портрета (чёрный фон + белый силуэт) для режима AdditiveBlending
function getEngravingPortraitTexture(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  if (engravingPortraitTextureCache.has(u)) {
    return engravingPortraitTextureCache.get(u);
  }
  const tex = remoteTextureLoader.load(
    u,
    () => {
      tex.needsUpdate = true;
    },
    undefined,
    (err) => {
      console.warn('Не загрузился SVG портрета:', u, err);
    }
  );
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 16;
  // Без перевода в sRGB: полутона от сглаживания не дают серую «плёнку» на камне
  if (tex.colorSpace !== undefined) tex.colorSpace = THREE.NoColorSpace;
  engravingPortraitTextureCache.set(u, tex);
  return tex;
}

function getMonumentTexture(textureUrl, element) {
  // Запасной вариант: если по элементу видно, что выбран бетон (colorName), но URL потерялся
  if (element && (element.colorName === 'Бетон' || element.stoneId === 'stone-concrete')) return concreteTexture;
  // Серый гранит: страхуемся по colorName/stoneId — если в админке очистили texture_url,
  // текстура всё равно подтянется (камень id=stone-grey, name_ru «Серый гранит»).
  if (element && (element.stoneId === 'stone-grey' || /серый гранит/i.test(element.colorName || ''))) return greyGraniteTexture;
  if (!textureUrl || typeof textureUrl !== 'string') return null;
  const decoded = tryDecodeUri(textureUrl);
  if (decoded.includes('красный-гранит')) return redGraniteTexture;
  if (decoded.includes('курты')) return kurtyGraniteTexture;
  if (decoded.includes('черный')) return blackGraniteTexture;
  if (decoded.includes('зеленый')) return greenGraniteTexture;
  if (decoded.includes('серый') || decoded.includes('grey') || decoded.includes('gray')) return greyGraniteTexture;
  if (decoded.includes('бетон') || decoded.includes('concrete')) return concreteTexture;
  if (decoded.includes('mramor') || decoded.includes('мрамор') || decoded.includes('marble')) return marbleTexture;
  return null;
}

// Сыпучие покрытия (крошка, гравий): как газон — без чёрной сетки «швов плитки»
function isLooseFloorMaterial(element) {
  if (!element) return false;
  const parts = [
    element.name,
    element.name_ru,
    element.colorName,
    element.texture_url,
    element.thumbnail_url,
  ]
    .filter(Boolean)
    .join(' ');
  let decoded = parts;
  try {
    decoded = decodeURIComponent(parts);
  } catch (_) {
    /* как есть */
  }
  const t = decoded.toLowerCase();
  return (
    t.includes('мрамор') ||
    t.includes('крошк') ||
    t.includes('marble') ||
    t.includes('mramor') ||
    t.includes('гравий') ||
    t.includes('щебен')
  );
}

// Для напольного покрытия: те же текстуры гранита/бетона/крошки, что и для памятников и цветника
function getFloorTexture(textureUrl, element) {
  // По названию из каталога — мраморная крошка (даже если в базе нет texture_url)
  if (element && isLooseFloorMaterial(element)) return marbleTexture;

  let u = textureUrl && typeof textureUrl === 'string' ? textureUrl.trim() : '';
  if (!u && element) {
    u = String(element.texture_url || element.thumbnail_url || '').trim();
  }
  if (!u) return null;
  // Внешняя ссылка (Supabase и т.п.)
  if (u.startsWith('http://') || u.startsWith('https://')) return getRemoteTexture(u);
  return getMonumentTexture(u, element);
}
function tryDecodeUri(url) {
  try { return decodeURIComponent(url); } catch (_) { return url; }
}

// Pamyatniki: арочная («мусульманская») форма стелы — прямоугольное тело +
// стрельчатая арка сверху. Габариты совпадают с BoxGeometry(w,h,d) и геометрия
// центрирована в начале координат, поэтому вся логика позиционирования и
// привязки гравировок к лицевой грани (z = +d/2) работает как с обычной коробкой.
// UV лицевой/задней грани нормализуются в 0..1, чтобы текстура гранита тайлилась
// так же, как на прямоугольных стелах (та же общая текстура с .repeat).
function buildArchedSteleGeometry(w, h, d) {
  const hw = w / 2;
  const archH = Math.min(w * 0.5, h * 0.45); // высота арочной части
  const bodyH = h - archH;                    // высота прямой части тела
  // Контур обходим против часовой (CCW) — конвенция THREE.Shape, чтобы лицевая
  // грань экструзии смотрела на +Z (к камере, туда же крепятся гравировки).
  const shape = new THREE.Shape();
  shape.moveTo(-hw, 0);
  shape.lineTo(hw, 0);                       // низ: лево → право
  shape.lineTo(hw, bodyH);                   // вверх по правой стороне
  shape.quadraticCurveTo(hw, h, 0, h);       // правое плечо → вершина (стрельчатая арка)
  shape.quadraticCurveTo(-hw, h, -hw, bodyH); // вершина → левое плечо
  shape.lineTo(-hw, 0);                       // вниз по левой стороне, замыкаем

  const uvGen = {
    generateTopUV(geometry, vertices, a, b, c) {
      const p = (i) => new THREE.Vector2((vertices[i * 3] + hw) / w, vertices[i * 3 + 1] / h);
      return [p(a), p(b), p(c)];
    },
    generateSideWallUV(geometry, vertices, a, b, c, e) {
      const p = (i) => new THREE.Vector2(vertices[i * 3] / Math.max(w, h), vertices[i * 3 + 1] / h);
      return [p(a), p(b), p(c), p(e)];
    },
  };

  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: d, bevelEnabled: false, steps: 1, curveSegments: 24, UVGenerator: uvGen,
  });
  geo.translate(0, -h / 2, -d / 2); // центрируем как BoxGeometry
  geo.computeVertexNormals();
  return geo;
}

// Распознаём арочную форму по названию каталожного элемента (как формы уже
// матчатся по «вертикальная»/«семейная»). Достаточно завести в админке памятник
// с «мусульман»/«арочн»/«мазар»/«арка» в названии — код отрисует арку.
function isArchedSteleForm(element) {
  const name = `${element.name_ru || ''} ${element.name || ''}`;
  return /мусульман|арочн|мазар|\bарка\b/i.test(name);
}

// Fetch GLB as blob first to avoid 400 errors when app is embedded in an iframe
// (cross-origin referer/origin headers can cause Supabase storage to reject direct loader requests)
async function loadGLBViaBlob(loader, url, onLoad, onProgress, onError) {
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    loader.load(
      blobUrl,
      (gltf) => { URL.revokeObjectURL(blobUrl); onLoad(gltf); },
      onProgress,
      (err) => { URL.revokeObjectURL(blobUrl); onError(err); }
    );
  } catch (err) {
    onError(err);
  }
}

// Стилизованная 3D-фигура человека ростом 170 см для шага «Размер»:
// низкополигональные примитивы, тёмно-серая матовая основа — служит эталоном
// масштаба рядом со стелой, не отвлекая от самого памятника.
function buildSizeRefHuman() {
  const group = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: 0x4a5568, roughness: 0.85, metalness: 0 });

  // Примитивная фигура-заглушка живёт в отдельной подгруппе: показывается мгновенно,
  // пока подгружается реалистичная GLB-модель (см. call-site эффекта шага «Размер»).
  // После загрузки GLB подгруппу убираем, не трогая подпись «170 см».
  const placeholder = new THREE.Group();
  placeholder.name = '__size-ref-placeholder__';

  const add = (geometry, x, y, z) => {
    const mesh = new THREE.Mesh(geometry, mat);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    placeholder.add(mesh);
  };

  add(new THREE.SphereGeometry(0.11, 18, 18),          0,    1.59, 0); // голова (макушка на 1.70 м = отметка «170»)
  add(new THREE.CylinderGeometry(0.05, 0.06, 0.08, 14), 0,    1.44, 0); // шея
  add(new THREE.CylinderGeometry(0.13, 0.16, 0.55, 18), 0,    1.12, 0); // торс
  add(new THREE.BoxGeometry(0.30, 0.10, 0.18),          0,    0.82, 0); // таз
  add(new THREE.CylinderGeometry(0.07, 0.06, 0.78, 14), -0.09, 0.40, 0); // левая нога
  add(new THREE.CylinderGeometry(0.07, 0.06, 0.78, 14),  0.09, 0.40, 0); // правая нога
  // Руки прижаты к торсу (inner-edge перекрывает плечо на ~1 см).
  add(new THREE.CylinderGeometry(0.045, 0.04, 0.62, 14), -0.17, 1.06, 0); // левая рука
  add(new THREE.CylinderGeometry(0.045, 0.04, 0.62, 14),  0.17, 1.06, 0); // правая рука
  group.add(placeholder);

  // Подпись «170 см» над головой — основной сигнал о росте. Самой шкалы рядом
  // нет: фигура уже даёт достаточный контекст масштаба относительно стелы.
  const tag = makeTextSprite('170 см', '#0f172a', 0.42, 0.18);
  tag.position.set(0, 1.92, 0);
  group.add(tag);

  group.name = '__size-ref-human__';
  return group;
}

// Билбордовая текстовая метка через CanvasTexture — всегда лицом к камере.
function makeTextSprite(text, color, scaleW, scaleH) {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 224;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'rgba(255, 255, 255, 0.94)';
  const r = 48, w = canvas.width, h = canvas.height;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(w, 0, w, h, r);
  ctx.arcTo(w, h, 0, h, r);
  ctx.arcTo(0, h, 0, 0, r);
  ctx.arcTo(0, 0, w, 0, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = color;
  ctx.font = 'bold 128px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 8;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
  }));
  sprite.scale.set(scaleW, scaleH, 1);
  sprite.renderOrder = 999;
  return sprite;
}

function disposeGroup(group) {
  group.traverse((obj) => {
    if (obj.isMesh) {
      obj.geometry?.dispose();
      if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
      else obj.material?.dispose();
    }
    if (obj.isSprite) {
      obj.material?.map?.dispose();
      obj.material?.dispose();
    }
  });
}

// Как disposeGroup, но дополнительно освобождает текстуры материалов мешей.
// Нужно для реалистичной GLB-фигуры-эталона (несёт JPEG-текстуру): без этого
// текстура утекает при каждом входе/выходе с шага «Размер». Вызывается ТОЛЬКО из
// pamyatniki-ветки (quizViewMode === 'size'), поэтому на blago-сборку не влияет.
function disposeSizeRefGroup(group) {
  group.traverse((obj) => {
    if (obj.isMesh) {
      obj.geometry?.dispose();
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((m) => {
        if (!m) return;
        m.map?.dispose();
        m.normalMap?.dispose();
        m.roughnessMap?.dispose();
        m.metalnessMap?.dispose();
        m.dispose();
      });
    }
    if (obj.isSprite) {
      obj.material?.map?.dispose();
      obj.material?.dispose();
    }
  });
}

export default function Scene3D() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const elementsRef = useRef({});
  const isDraggingRef = useRef(false);
  const dragPlaneRef = useRef(null);
  const offsetRef = useRef(new THREE.Vector3());
  const gltfLoaderRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const [sceneReady, setSceneReady] = useState(false);
  const [texturesReady, setTexturesReady] = useState(_initialTexturesLoaded);

  useEffect(() => {
    if (!_initialTexturesLoaded) {
      _textureReadyCallbacks.push(() => setTexturesReady(true));
    }
    const timer = setTimeout(() => setTexturesReady(true), 6000);
    return () => clearTimeout(timer);
  }, []);
  
  // Initialize GLTF loader with DRACO support
  useEffect(() => {
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    
    const gltfLoader = new GLTFLoader();
    gltfLoader.setDRACOLoader(dracoLoader);
    gltfLoaderRef.current = gltfLoader;
    
    return () => {
      dracoLoader.dispose();
    };
  }, []);
  
  // Subscribe to store with selectors
  const perimeterWidth = useStore((state) => state.perimeterWidth);
  const perimeterLength = useStore((state) => state.perimeterLength);
  const placedElements = useStore((state) => state.placedElements);
  const selectedPlacedId = useStore((state) => state.selectedPlacedId);
  const setSelectedPlacedId = useStore((state) => state.setSelectedPlacedId);
  const updatePlacedElement = useStore((state) => state.updatePlacedElement);
  const selectedFloor = useStore((state) => state.selectedFloor);
  const selectedFoundation = useStore((state) => state.selectedFoundation);
  const selectedFence = useStore((state) => state.selectedFence);
  const selectedBorder = useStore((state) => state.selectedBorder);
  const flowerbedFilling = useStore((state) => state.flowerbedFilling); // null = земля, объект = выбранное покрытие
  const setResetCameraCallback = useStore((state) => state.setResetCameraCallback);
  const setModelLoading = useStore((state) => state.setModelLoading);
  const clearModelLoading = useStore((state) => state.clearModelLoading);
  const loadingModels = useStore((state) => state.loadingModels);
  const categories = useStore((state) => state.categories);
  const pendingMonument = useStore((state) => state.pendingMonument);
  const pendingElement = useStore((state) => state.pendingElement);
  const quizViewMode = useStore((state) => state.quizViewMode);
  const previewMeshRef = useRef(null);
  const genericPreviewMeshRef = useRef(null);
  const sizeRefHumanRef = useRef(null);
  
  // Check if any models are loading
  const isLoading = Object.values(loadingModels).some(Boolean);
  const loadingCount = Object.values(loadingModels).filter(Boolean).length;

  // Layer heights calculation
  const getLayerHeights = useCallback(() => {
    // Layer 1: Foundation (земля/бетон)
    const foundationHeight = selectedFoundation ? selectedFoundation.height : 0;
    // Layer 2: Floor - on top of foundation
    const floorHeight = selectedFloor ? selectedFloor.height : 0;
    // Layer 3: Elements - на поверхности пола (без зазора, чтобы не «висело»)
    const baseY = foundationHeight + floorHeight;
    
    return { foundationHeight, floorHeight, baseY };
  }, [selectedFoundation, selectedFloor]);

  // Тумба всегда горизонтальная: если с бэкенда пришли вертикальные размеры (height > width), меняем местами
  const getPedestalDimensions = useCallback((element) => {
    const w = element.width ?? 0.25;
    const h = element.height ?? 0.5;
    const d = element.depth ?? 0.25;
    if (h > w) return { w: h, h: w, d }; // было вертикально — делаем горизонтально
    return { w, h, d };
  }, []);

  // Helper function to create primitive mesh
  const createPrimitiveMesh = useCallback((element, material, categories = []) => {
    const group = new THREE.Group();
    group.userData.elementId = element.id;
    group.userData.isPlacedElement = true;

    const category = categories.find(c => c.id === element.category_id);
    const isFlowerbed = element.model_type === 'flowerbed' || category?.name === 'flowerbed';

    // Тумба: тот же материал, что и памятники (текстура/цвет из выбранного камня)
    const meshMaterial = material;

    const borderThick = 0.08;

    // ---- Engraving portrait ----
    if (element.model_type === 'engraving_portrait') {
      const w = element.width || 0.25;
      const h = element.height || 0.35;
      let mat;
      if (element.texture_url) {
        // SVG с Supabase: AdditiveBlending — чёрный фон не осветляет камень, белое добавляется
        // Отдельная текстура без sRGB, иначе по краям силуэта появляется серая кайма
        const tex = getEngravingPortraitTexture(element.texture_url);
        mat = new THREE.MeshBasicMaterial({
          map: tex,
          side: THREE.DoubleSide,
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        });
      } else {
        // Fallback: canvas-drawn silhouette
        const MAX_RES = 512;
        const aspect = w / h;
        const canvasW = aspect >= 1 ? MAX_RES : Math.round(MAX_RES * aspect);
        const canvasH = aspect >= 1 ? Math.round(MAX_RES / aspect) : MAX_RES;
        const canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;
        drawPortraitSilhouette(canvas);
        const texture = new THREE.CanvasTexture(canvas);
        mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, transparent: true, alphaTest: 0.05 });
      }
      const geo = new THREE.PlaneGeometry(w, h);
      const plane = new THREE.Mesh(geo, mat);
      plane.userData.elementId = element.id;
      plane.userData.isPlacedElement = true;
      group.add(plane);
      group.userData.lastWidth = w;
      group.userData.lastHeight = h;
      group.userData.lastDepth = 0.005;
      group.userData.isFlowerbed = false;
      return group;
    }

    // ---- Engraving text (ФИО) ----
    if (element.model_type === 'engraving_text') {
      const w = element.width || 0.28;
      const h = element.height || 0.18;
      const MAX_RES = 512;
      const aspect = w / h;
      const canvasW = aspect >= 1 ? MAX_RES : Math.round(MAX_RES * aspect);
      const canvasH = aspect >= 1 ? Math.round(MAX_RES / aspect) : MAX_RES;
      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;
      drawFIOCanvas(canvas, element);
      const texture = new THREE.CanvasTexture(canvas);
      const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, transparent: true, alphaTest: 0.05 });
      const geo = new THREE.PlaneGeometry(w, h);
      const plane = new THREE.Mesh(geo, mat);
      plane.userData.elementId = element.id;
      plane.userData.isPlacedElement = true;
      group.add(plane);
      group.userData.lastWidth = w;
      group.userData.lastHeight = h;
      group.userData.lastDepth = 0.005;
      group.userData.isFlowerbed = false;
      // Store refs for live text updates
      group.userData.engravingCanvas = canvas;
      group.userData.engravingTexture = texture;
      group.userData.lastLastName = element.lastName || '';
      group.userData.lastFirstName = element.firstName || '';
      group.userData.lastPatronymic = element.patronymic || '';
      group.userData.lastLastNameSize = element.lastNameSize || 20;
      group.userData.lastFirstNameSize = element.firstNameSize || 14;
      group.userData.lastPatronymicSize = element.patronymicSize || 14;
      return group;
    }

    // ---- Engraving epitaph (одна курсивная строка внизу) ----
    if (element.model_type === 'engraving_epitaph') {
      const w = element.width || 0.4;
      const h = element.height || 0.06;
      const MAX_RES = 512;
      const aspect = w / h;
      const canvasW = aspect >= 1 ? MAX_RES : Math.round(MAX_RES * aspect);
      const canvasH = aspect >= 1 ? Math.round(MAX_RES / aspect) : MAX_RES;
      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;
      drawEpitaphCanvas(canvas, element);
      const texture = new THREE.CanvasTexture(canvas);
      const mat = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, transparent: true, alphaTest: 0.05 });
      const geo = new THREE.PlaneGeometry(w, h);
      const plane = new THREE.Mesh(geo, mat);
      plane.userData.elementId = element.id;
      plane.userData.isPlacedElement = true;
      group.add(plane);
      group.userData.lastWidth = w;
      group.userData.lastHeight = h;
      group.userData.lastDepth = 0.005;
      group.userData.isFlowerbed = false;
      group.userData.engravingCanvas = canvas;
      group.userData.engravingTexture = texture;
      group.userData.lastEpitaphText = element.epitaphText || '';
      return group;
    }

    if (isFlowerbed) {
      const w = element.width;
      const h = element.height;
      const d = element.depth;
      const isRectangle = element.flowerbed_shape === 'rectangle';
      if (isRectangle) {
        // Цветник — сплошной прямоугольник
        const rectGeo = new THREE.BoxGeometry(w, h, d);
        const rectMesh = new THREE.Mesh(rectGeo, meshMaterial);
        rectMesh.position.set(0, h / 2, 0);
        rectMesh.castShadow = true;
        rectMesh.receiveShadow = true;
        rectMesh.userData.elementId = element.id;
        rectMesh.userData.isPlacedElement = true;
        group.add(rectMesh);
      } else {
        // Цветник — форма П (три бордюра + дно с повторяющейся текстурой)
        const leftGeo = new THREE.BoxGeometry(borderThick, h, d);
        const leftMesh = new THREE.Mesh(leftGeo, meshMaterial);
        leftMesh.position.set(-w / 2 + borderThick / 2, h / 2, 0);
        leftMesh.castShadow = true;
        leftMesh.receiveShadow = true;
        leftMesh.userData.elementId = element.id;
        leftMesh.userData.isPlacedElement = true;
        group.add(leftMesh);
        const frontGeo = new THREE.BoxGeometry(w, h, borderThick);
        const frontMesh = new THREE.Mesh(frontGeo, meshMaterial.clone());
        frontMesh.position.set(0, h / 2, d / 2 - borderThick / 2);
        frontMesh.castShadow = true;
        frontMesh.receiveShadow = true;
        frontMesh.userData.elementId = element.id;
        frontMesh.userData.isPlacedElement = true;
        group.add(frontMesh);
        const rightGeo = new THREE.BoxGeometry(borderThick, h, d);
        const rightMesh = new THREE.Mesh(rightGeo, meshMaterial.clone());
        rightMesh.position.set(w / 2 - borderThick / 2, h / 2, 0);
        rightMesh.castShadow = true;
        rightMesh.receiveShadow = true;
        rightMesh.userData.elementId = element.id;
        rightMesh.userData.isPlacedElement = true;
        group.add(rightMesh);
        // Дно внутри П — плоскость с повторяющейся текстурой (земля по умолчанию, либо выбранное покрытие)
        const innerW = Math.max(0.01, w - 2 * borderThick);
        const innerD = Math.max(0.01, d - borderThick);
        const interiorGeo = new THREE.PlaneGeometry(innerW, innerD);
        const isGrassFill = flowerbedFilling && (flowerbedFilling.name === 'grass' || flowerbedFilling.name_ru === 'Трава');
        const isMarbleFill = flowerbedFilling && (
          (flowerbedFilling.name || '').includes('marble') ||
          (flowerbedFilling.name_ru || '').includes('Мраморная') ||
          (flowerbedFilling.texture_url || flowerbedFilling.thumbnail_url || '').includes('mramor')
        );
        const fillTex = flowerbedFilling
          ? (isGrassFill
              ? grassTexture
              : isMarbleFill
                ? marbleTextureFlowerbed
                : getFloorTexture(
                    flowerbedFilling.texture_url || flowerbedFilling.thumbnail_url,
                    flowerbedFilling
                  ))
          : flowerbedInteriorTexture;
        if (fillTex) {
          fillTex.wrapS = fillTex.wrapT = THREE.RepeatWrapping;
          // Мраморная крошка в цветнике: такой же масштаб как на полу
          const repeatScale = isMarbleFill ? (1 / 0.7) : 1;
          fillTex.repeat.set(innerW * repeatScale, innerD * repeatScale);
          fillTex.needsUpdate = true;
        }
        const interiorMat = new THREE.MeshStandardMaterial({
          map: fillTex || null,
          color: fillTex ? 0xffffff : 0x6b4f1d, // если нет текстуры — коричневый цвет земли
          roughness: 0.9,
          metalness: 0.05
        });
        const interiorMesh = new THREE.Mesh(interiorGeo, interiorMat);
        interiorMesh.rotation.x = -Math.PI / 2;
        interiorMesh.position.set(0, 0.005, -borderThick / 2);
        interiorMesh.receiveShadow = true;
        interiorMesh.userData.elementId = element.id;
        interiorMesh.userData.isPlacedElement = true;
        group.add(interiorMesh);
      }
      group.userData.lastWidth = w;
      group.userData.lastHeight = h;
      group.userData.lastDepth = d;
      group.userData.isFlowerbed = true;
      group.userData.flowerbedShape = isRectangle ? 'rectangle' : 'p';
      return group;
    }

    let geometry;
    let effW = element.width, effH = element.height, effD = element.depth;
    if (element.model_type === 'pedestal') {
      const ped = getPedestalDimensions(element);
      effW = ped.w;
      effH = ped.h;
      effD = ped.d;
    }
    if (element.model_type === 'cylinder') {
      geometry = new THREE.CylinderGeometry(element.width / 2, element.width / 2, element.height, 32);
    } else if (isPamyatniki && isArchedSteleForm(element)) {
      // Мусульманская/арочная стела — стрельчатый силуэт вместо коробки
      geometry = buildArchedSteleGeometry(effW, effH, effD);
    } else {
      geometry = new THREE.BoxGeometry(effW, effH, effD);
    }

    const mainMesh = new THREE.Mesh(geometry, meshMaterial);
    mainMesh.geometry.computeBoundingSphere();
    mainMesh.geometry.computeBoundingBox();
    mainMesh.castShadow = true;
    mainMesh.receiveShadow = true;
    mainMesh.userData.elementId = element.id;
    mainMesh.userData.isPlacedElement = true;
    group.add(mainMesh);
    
    // Store dimensions for change detection (для тумбы — эффективные горизонтальные)
    group.userData.lastWidth = effW;
    group.userData.lastHeight = effH;
    group.userData.lastDepth = effD;
    group.userData.isFlowerbed = false;

    if (element.model_type === 'cross') {
      const crossGeo = new THREE.BoxGeometry(element.width * 1.5, element.height * 0.12, element.depth);
      const crossMesh = new THREE.Mesh(crossGeo, meshMaterial.clone());
      crossMesh.position.set(0, element.height * 0.25, 0);
      crossMesh.castShadow = true;
      crossMesh.userData.elementId = element.id;
      crossMesh.userData.isPlacedElement = true;
      group.add(crossMesh);
    }

    if (element.model_type === 'bench') {
      const backGeo = new THREE.BoxGeometry(element.width, element.height * 0.6, 0.05);
      const backMesh = new THREE.Mesh(backGeo, meshMaterial.clone());
      backMesh.position.set(0, element.height * 0.3, -element.depth / 2 + 0.03);
      backMesh.castShadow = true;
      backMesh.userData.elementId = element.id;
      backMesh.userData.isPlacedElement = true;
      group.add(backMesh);
    }

    if (element.model_type === 'table') {
      const legGeo = new THREE.BoxGeometry(0.05, element.height * 0.9, 0.05);
      const positions = [
        [-element.width / 2 + 0.05, -element.height * 0.45, -element.depth / 2 + 0.05],
        [element.width / 2 - 0.05, -element.height * 0.45, -element.depth / 2 + 0.05],
        [-element.width / 2 + 0.05, -element.height * 0.45, element.depth / 2 - 0.05],
        [element.width / 2 - 0.05, -element.height * 0.45, element.depth / 2 - 0.05]
      ];
      positions.forEach(([x, y, z]) => {
        const leg = new THREE.Mesh(legGeo, meshMaterial.clone());
        leg.position.set(x, y, z);
        leg.castShadow = true;
        leg.userData.elementId = element.id;
        leg.userData.isPlacedElement = true;
        group.add(leg);
      });
    }

    return group;
  }, [getPedestalDimensions, categories, flowerbedFilling]);

  // Sync elements with scene
  // Helper: find a pedestal placed beneath the given element (by X,Z overlap)
  // Only monument-category elements (category name='monument') react to pedestal
  const getPedestalBeneath = useCallback((element, allElements, categories) => {
    // Determine if this element is a monument by checking its category
    const elementCategory = categories.find(c => c.id === element.category_id);
    const isMonument = elementCategory?.name === 'monument';
    if (!isMonument) return null;
    
    const pedestals = allElements.filter(el => el.model_type === 'pedestal' && el.id !== element.id);
    for (const ped of pedestals) {
      const dims = getPedestalDimensions(ped);
      const dx = Math.abs(element.position.x - ped.position.x);
      const dz = Math.abs(element.position.z - ped.position.z);
      const halfPedW = dims.w / 2 + 0.10;
      const halfPedD = dims.d / 2 + 0.10;
      if (dx < halfPedW && dz < halfPedD) return ped;
    }
    return null;
  }, [getPedestalDimensions]);

  const syncElements = useCallback(() => {
    if (!sceneRef.current || !sceneReady) return;
    const scene = sceneRef.current;
    const { baseY } = getLayerHeights();

    // Remove old elements
    Object.keys(elementsRef.current).forEach((id) => {
      if (!placedElements.find((el) => el.id === id)) {
        const mesh = elementsRef.current[id];
        scene.remove(mesh);
        delete elementsRef.current[id];
      }
    });

    // Add or update elements
    placedElements.forEach((element) => {
      let mesh = elementsRef.current[element.id];

      if (!mesh) {
        const monumentTexture = getMonumentTexture(element.texture_url, element);
        const useTexture = !!monumentTexture;
        const material = new THREE.MeshStandardMaterial(
          useTexture
            ? { map: monumentTexture, color: 0xffffff, roughness: 0.7, metalness: 0.1 }
            : { color: element.color || '#666666', roughness: 0.7, metalness: 0.1 }
        );

        if (element.model_url && (element.model_url.endsWith('.glb') || element.model_url.endsWith('.gltf'))) {
          // Check if loader is ready
          if (!gltfLoaderRef.current) {
            console.warn('GLTFLoader not ready yet');
            return;
          }
          
          const group = new THREE.Group();
          group.userData.elementId = element.id;
          group.userData.isPlacedElement = true;
          
          // Set loading state
          setModelLoading(element.id, true);
          
          // Build full URL for model (fallback как в api/index.js, иначе undefined + path ломает загрузку → прямоугольник)
          const backendBase = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
          const modelUrl = element.model_url.startsWith('http') 
            ? element.model_url 
            : `${backendBase}${element.model_url}`;
          
          loadGLBViaBlob(
            gltfLoaderRef.current,
            modelUrl,
            (gltf) => {
              const model = gltf.scene;
              model.userData.elementId = element.id;
              model.userData.isPlacedElement = true;
              
              const box = new THREE.Box3().setFromObject(model);
              const size = box.getSize(new THREE.Vector3());
              const center = box.getCenter(new THREE.Vector3());
              const maxDim = Math.max(size.x, size.y, size.z);
              const scale = Math.max(element.width, element.height, element.depth) / maxDim;
              model.scale.setScalar(scale);
              
              // Store original size and center for later scale updates
              group.userData.originalSize = size.clone();
              group.userData.originalCenter = center.clone();
              group.userData.lastWidth = element.width;
              group.userData.lastHeight = element.height;
              group.userData.lastDepth = element.depth;
              
              model.traverse((child) => {
                if (child.isMesh) {
                  child.castShadow = true;
                  child.receiveShadow = true;
                  child.userData.elementId = element.id;
                  child.userData.isPlacedElement = true;
                  
                  // Текстура красного гранита или свой цвет
                  if (child.material) {
                    const tex = getMonumentTexture(element.texture_url, element);
                    if (tex) {
                      if (Array.isArray(child.material)) {
                        child.material = child.material.map(mat => {
                          const newMat = mat.clone();
                          newMat.map = tex;
                          newMat.color = new THREE.Color(0xffffff);
                          return newMat;
                        });
                      } else {
                        child.material = child.material.clone();
                        child.material.map = tex;
                        child.material.color = new THREE.Color(0xffffff);
                      }
                    } else if (element.color && element.color.trim() !== '') {
                      if (Array.isArray(child.material)) {
                        child.material = child.material.map(mat => {
                          const newMat = mat.clone();
                          newMat.color = new THREE.Color(element.color);
                          return newMat;
                        });
                      } else {
                        child.material = child.material.clone();
                        child.material.color = new THREE.Color(element.color);
                      }
                    }
                  }
                }
              });
              
              group.add(model);
              
              // Position correctly after model loads: place bottom of model on baseY
              const { baseY: currentBaseY } = getLayerHeights();
              const scaledHalfHeight = (size.y * scale) / 2;
              const centerOffsetY = center.y * scale;
              group.position.y = currentBaseY + scaledHalfHeight - centerOffsetY;
              
              // Clear loading state
              clearModelLoading(element.id);
            },
            (progress) => {
              // Progress callback - model is still loading
              if (progress.total > 0) {
                const percent = Math.round((progress.loaded / progress.total) * 100);
                console.log(`Loading model ${element.name_ru}: ${percent}%`);
              }
            },
            (error) => {
              console.error('Error loading GLB model:', error);
              const primitive = createPrimitiveMesh(element, material, categories);
              primitive.children.forEach(child => group.add(child.clone()));
              // Clear loading state on error
              clearModelLoading(element.id);
            }
          );
          
          scene.add(group);
          elementsRef.current[element.id] = group;
          mesh = group;
        } else {
          mesh = createPrimitiveMesh(element, material, categories);
          scene.add(mesh);
          elementsRef.current[element.id] = mesh;
        }
      }

      // Update position and rotation
      if (mesh) {
        // Пересоздать примитив, если тип цветник/не-цветник или форма цветника изменилась
        const elementCategory = categories.find(c => c.id === element.category_id);
        const isFlowerbed = element.model_type === 'flowerbed' || elementCategory?.name === 'flowerbed';
        const flowerbedShape = element.flowerbed_shape === 'rectangle' ? 'rectangle' : 'p';
        const typeChanged = !element.model_url && (
          mesh.userData.isFlowerbed !== isFlowerbed ||
          (isFlowerbed && mesh.userData.flowerbedShape !== flowerbedShape)
        );
        if (typeChanged) {
          scene.remove(mesh);
          delete elementsRef.current[element.id];
          const monumentTexture = getMonumentTexture(element.texture_url, element);
          const useTexture = !!monumentTexture;
          const material = new THREE.MeshStandardMaterial(
            useTexture
              ? { map: monumentTexture, color: 0xffffff, roughness: 0.7, metalness: 0.1 }
              : { color: element.color || '#666666', roughness: 0.7, metalness: 0.1 }
          );
          mesh = createPrimitiveMesh(element, material, categories);
          scene.add(mesh);
          elementsRef.current[element.id] = mesh;
        }

        // Для тумбы сравниваем эффективные (горизонтальные) размеры
        const isPedestal = element.model_type === 'pedestal';
        const effDims = isPedestal ? getPedestalDimensions(element) : { w: element.width, h: element.height, d: element.depth };
        const dimensionsChanged = mesh.userData.lastWidth !== effDims.w ||
            mesh.userData.lastHeight !== effDims.h ||
            mesh.userData.lastDepth !== effDims.d;

        if (dimensionsChanged) {
          if (element.model_url) {
            // For GLB models - update scale and recalculate Y position using Box3
            if (mesh.userData.originalSize) {
              const maxDim = Math.max(mesh.userData.originalSize.x, mesh.userData.originalSize.y, mesh.userData.originalSize.z);
              const newScale = Math.max(element.width, element.height, element.depth) / maxDim;
              mesh.scale.setScalar(newScale);
            }
          } else {
            // For primitive meshes - recreate with new dimensions (сохраняем текстуру красного гранита)
            scene.remove(mesh);
            delete elementsRef.current[element.id];
            const monumentTexture = getMonumentTexture(element.texture_url, element);
            const useTexture = !!monumentTexture;
            const material = new THREE.MeshStandardMaterial(
              useTexture
                ? { map: monumentTexture, color: 0xffffff, roughness: 0.7, metalness: 0.1 }
                : { color: element.color || '#666666', roughness: 0.7, metalness: 0.1 }
            );
            const newMesh = createPrimitiveMesh(element, material, categories);
            scene.add(newMesh);
            elementsRef.current[element.id] = newMesh;
            mesh = newMesh;
          }
        }

        // Live update for engraving_text: redraw canvas when FIO fields change
        if (element.model_type === 'engraving_text' &&
            mesh.userData.engravingCanvas && mesh.userData.engravingTexture) {
          const changed =
            mesh.userData.lastLastName !== (element.lastName || '') ||
            mesh.userData.lastFirstName !== (element.firstName || '') ||
            mesh.userData.lastPatronymic !== (element.patronymic || '') ||
            mesh.userData.lastLastNameSize !== (element.lastNameSize || 20) ||
            mesh.userData.lastFirstNameSize !== (element.firstNameSize || 14) ||
            mesh.userData.lastPatronymicSize !== (element.patronymicSize || 14);
          if (changed) {
            drawFIOCanvas(mesh.userData.engravingCanvas, element);
            mesh.userData.engravingTexture.needsUpdate = true;
            mesh.userData.lastLastName = element.lastName || '';
            mesh.userData.lastFirstName = element.firstName || '';
            mesh.userData.lastPatronymic = element.patronymic || '';
            mesh.userData.lastLastNameSize = element.lastNameSize || 20;
            mesh.userData.lastFirstNameSize = element.firstNameSize || 14;
            mesh.userData.lastPatronymicSize = element.patronymicSize || 14;
          }
        }

        // Live update for engraving_epitaph: redraw when the epitaph text changes
        if (element.model_type === 'engraving_epitaph' &&
            mesh.userData.engravingCanvas && mesh.userData.engravingTexture) {
          if (mesh.userData.lastEpitaphText !== (element.epitaphText || '')) {
            drawEpitaphCanvas(mesh.userData.engravingCanvas, element);
            mesh.userData.engravingTexture.needsUpdate = true;
            mesh.userData.lastEpitaphText = element.epitaphText || '';
          }
        }

        // Поворот задаём после возможной замены меша, чтобы новый меш не сбрасывался в 0
        mesh.rotation.y = element.rotation * (Math.PI / 180);
        
        // Store current dimensions for comparison (для тумбы — эффективные)
        if (mesh.userData) {
          mesh.userData.lastWidth = effDims.w;
          mesh.userData.lastHeight = effDims.h;
          mesh.userData.lastDepth = effDims.d;
        }

        // Calculate correct Y position using actual bounding box
        // For primitives: height/2 above surfaceY (geometry is centered)
        // For GLB: use Box3 to find exact bottom of model
        // If element is a monument AND a pedestal is beneath it -> raise by pedestal height
        let surfaceY = baseY;
        if (element.model_type !== 'pedestal') {
          const ped = getPedestalBeneath(element, placedElements, categories);
          if (ped) {
            const pedH = ped.model_type === 'pedestal' ? getPedestalDimensions(ped).h : ped.height;
            surfaceY = baseY + pedH;
          }
        }
        
        let posY;
        if (element.model_url && mesh.userData.originalSize) {
          const maxDim = Math.max(mesh.userData.originalSize.x, mesh.userData.originalSize.y, mesh.userData.originalSize.z);
          const currentScale = Math.max(element.width, element.height, element.depth) / maxDim;
          const scaledHalfHeight = (mesh.userData.originalSize.y * currentScale) / 2;
          const centerOffsetY = mesh.userData.originalCenter ? mesh.userData.originalCenter.y * currentScale : 0;
          posY = surfaceY + scaledHalfHeight - centerOffsetY;
        } else if (!element.model_url) {
          const elementCategory = categories.find(c => c.id === element.category_id);
          const isFlowerbed = element.model_type === 'flowerbed' || elementCategory?.name === 'flowerbed';
          // Цветник: группа имеет низ в локальной y=0, ставим группу на поверхность
          if (isFlowerbed) {
            posY = surfaceY;
          } else if (element.model_type === 'pedestal') {
            posY = surfaceY + getPedestalDimensions(element).h / 2;
          } else {
            posY = surfaceY + element.height / 2;
          }
        } else {
          // GLB not loaded yet - use stored position
          posY = element.position.y + baseY;
        }
        
        // Auto-attach engraving elements to monument front face
        if (element.model_type === 'engraving_portrait' || element.model_type === 'engraving_text' || element.model_type === 'engraving_epitaph') {
          const monument = placedElements.find(el => {
            const cat = categories.find(c => c.id === el.category_id);
            return cat?.name === 'monument' && el.id !== element.id;
          });
          if (monument) {
            const monH = monument.height || 1;
            const monD = monument.depth || 0.08;
            const monZ = monument.position.z;
            // Place just in front of monument face (toward camera = higher Z)
            const engZ = monZ + monD / 2 + 0.003;
            // Anchor from monument's actual bottom — accounts for a pedestal underneath
            // (without this, baseY-relative offsets float the engraving above the monument).
            const pedBeneath = getPedestalBeneath(monument, placedElements, categories);
            const pedH = pedBeneath ? getPedestalDimensions(pedBeneath).h : 0;
            const monumentBottomY = baseY + pedH;
            // Family stele is wider than tall — portrait and ФИО need to stack
            // tightly on each face. Anchor portrait to top, ФИО to bottom with a small margin.
            // For tall vertical steles use the original upper-area / middle ratios.
            const isHorizontalStele = (monument.width || 1) > (monument.height || 1);
            const margin = 0.04;
            // На короткой горизонтальной (семейной) стеле портрет (~0.35) + ФИО (~0.18)
            // + поля по высоте не помещаются и налезают друг на друга. Масштабируем
            // обе гравировки так, чтобы стопка влезла в высоту стелы. Вертикальные
            // стелы высокие — их не трогаем (engScale = 1).
            let engScale = 1;
            if (isHorizontalStele) {
              const stackNeed = 0.35 + 0.18 + 2 * margin + 0.06; // портрет + ФИО + поля + зазор
              if (stackNeed > monH) engScale = Math.max(0.5, monH / stackNeed);
            }
            mesh.scale.set(engScale, engScale, 1);
            const halfH = ((element.height || 0.3) * engScale) / 2;
            // Pamyatniki: гравировку полумесяца сажаем в вершину арки (маленькую, сверху),
            // а не в зону портрета. Для blago условие сворачивается Терсером.
            const isMoonEng = isPamyatniki && /полумесяц|\bлуна\b|moon/i.test(`${element.name_ru || ''} ${element.name || ''}`);
            // На арочной (мусульманской) стеле верх занят полумесяцем — опускаем
            // портрет и ФИО ниже (≈ на 10% высоты), чтобы они не налезали на луну
            // в вершине арки. Вертикальная/семейная — без изменений.
            const isArched = isPamyatniki && isArchedSteleForm(monument);
            const portraitRatio = isArched ? 0.62 : 0.72;
            const fioRatio = isArched ? 0.35 : 0.45;
            const autoEngY = isMoonEng
              ? monumentBottomY + monH * 0.94 - halfH
              : element.model_type === 'engraving_epitaph'
              // Эпитафия — внизу грани, под ФИО (как на реальных памятниках).
              ? monumentBottomY + monH * (isHorizontalStele ? 0.10 : 0.15) + halfH
              : element.model_type === 'engraving_portrait'
              ? (isHorizontalStele
                  ? monumentBottomY + monH - margin - halfH
                  : monumentBottomY + monH * portraitRatio)
              : (isHorizontalStele
                  ? monumentBottomY + margin + halfH
                  : monumentBottomY + monH * fioRatio);
            const engY = autoEngY + (element.engravingOffsetY || 0);
            const autoEngX = monument.position.x;
            const engX = autoEngX + (element.engravingOffsetX || 0);
            mesh.position.set(engX, engY, engZ);
          } else {
            mesh.position.set(element.position.x, posY, element.position.z);
          }
        } else {
          mesh.position.set(element.position.x, posY, element.position.z);
        }

        // Update emissive for selection
        mesh.traverse((child) => {
          if (child.isMesh && child.material) {
            const isSelected = element.id === selectedPlacedId;
            if (child.material.emissive) {
              child.material.emissive = new THREE.Color(isSelected ? 0xc5a065 : 0x000000);
              child.material.emissiveIntensity = isSelected ? 0.3 : 0;
            }
          }
        });
      }
    });
  }, [placedElements, selectedPlacedId, getLayerHeights, createPrimitiveMesh, sceneReady, setModelLoading, clearModelLoading, getPedestalBeneath, categories]);

  // Превью памятника при выборе цвета (показываем на сцене до нажатия «Выбрать»)
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !sceneReady) return;

    const removePreviewMesh = (mesh) => {
      if (!mesh) return;
      if (sceneRef.current) sceneRef.current.remove(mesh);
      mesh.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    };

    // Удалить любой старый превью (в т.ч. «забытый» в сцене)
    if (previewMeshRef.current) {
      removePreviewMesh(previewMeshRef.current);
      previewMeshRef.current = null;
    }
    scene.children.filter(c => c.userData && c.userData.isPreview).forEach(orphan => {
      scene.remove(orphan);
      removePreviewMesh(orphan);
    });

    if (!pendingMonument) return;

    const { baseY } = getLayerHeights();
    const monumentTexture = getMonumentTexture(pendingMonument.texture_url, pendingMonument);
    const useTexture = !!monumentTexture;
    const material = new THREE.MeshStandardMaterial(
      useTexture
        ? { map: monumentTexture, color: 0xffffff, roughness: 0.7, metalness: 0.1, transparent: true, opacity: 0.92 }
        : { color: pendingMonument.color || '#666666', roughness: 0.7, metalness: 0.1, transparent: true, opacity: 0.92 }
    );
    const previewMesh = createPrimitiveMesh(
      { ...pendingMonument, id: '__preview__' },
      material,
      categories
    );
    previewMesh.userData.isPreview = true;
    previewMesh.userData.elementId = '__preview__';
    previewMesh.traverse((child) => {
      if (child.userData) child.userData.isPreview = true;
      if (child.raycast) child.raycast = () => {};
    });
    const posY = baseY + (pendingMonument.height || 0.5) / 2;
    previewMesh.position.set(0, posY, 0);
    scene.add(previewMesh);
    previewMeshRef.current = previewMesh;

    return () => {
      if (previewMeshRef.current) {
        removePreviewMesh(previewMeshRef.current);
        previewMeshRef.current = null;
      }
    };
  }, [pendingMonument, getLayerHeights, createPrimitiveMesh, sceneReady]);

  // Полупрозрачное превью любого обычного объекта каталога (лавки, столы, цветники и т.д.)
  // до нажатия "Добавить на сцену" в сайдбаре.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene || !sceneReady) return;

    const removePreviewMesh = (mesh) => {
      if (!mesh) return;
      if (sceneRef.current) sceneRef.current.remove(mesh);
      mesh.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    };

    // Удаляем старое превью обычного объекта
    if (genericPreviewMeshRef.current) {
      removePreviewMesh(genericPreviewMeshRef.current);
      genericPreviewMeshRef.current = null;
    }
    scene.children
      .filter(c => c.userData && c.userData.isPreviewGeneric)
      .forEach(orphan => {
        scene.remove(orphan);
        removePreviewMesh(orphan);
      });

    if (!pendingElement) return;

    const { baseY } = getLayerHeights();
    const material = new THREE.MeshStandardMaterial({
      color: pendingElement.color || '#666666',
      roughness: 0.7,
      metalness: 0.1,
      transparent: true,
      opacity: 0.65,
    });

    const placePreview = (group) => {
      group.userData.isPreviewGeneric = true;
      group.userData.elementId = '__preview_generic__';
      group.traverse((child) => {
        if (child.userData) child.userData.isPreviewGeneric = true;
        if (child.isMesh && child.material) {
          child.material = child.material.clone();
          child.material.transparent = true;
          child.material.opacity = 0.65;
        }
        if (child.raycast) child.raycast = () => {};
      });
      const height = pendingElement.height || 0.5;
      group.position.set(0, baseY + height / 2, 0);
      scene.add(group);
      genericPreviewMeshRef.current = group;
    };

    const hasGlb = pendingElement.model_url &&
      (pendingElement.model_url.endsWith('.glb') || pendingElement.model_url.endsWith('.gltf'));

    if (hasGlb && gltfLoaderRef.current) {
      const backendBase = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const modelUrl = pendingElement.model_url.startsWith('http')
        ? pendingElement.model_url
        : `${backendBase}${pendingElement.model_url}`;

      let cancelled = false;
      loadGLBViaBlob(
        gltfLoaderRef.current,
        modelUrl,
        (gltf) => {
          if (cancelled) return;
          const group = gltf.scene;
          // Масштабируем по максимальному измерению элемента
          const box = new THREE.Box3().setFromObject(group);
          const size = new THREE.Vector3();
          box.getSize(size);
          const maxDim = Math.max(size.x, size.y, size.z);
          if (maxDim > 0) {
            const targetSize = Math.max(pendingElement.width || 1, pendingElement.height || 1, pendingElement.depth || 1);
            group.scale.setScalar(targetSize / maxDim);
          }
          placePreview(group);
        },
        null,
        () => {
          if (cancelled) return;
          // Если GLB не загрузился — fallback на примитив
          const fallback = createPrimitiveMesh({ ...pendingElement, id: '__preview_generic__' }, material, categories);
          placePreview(fallback);
        }
      );
      return () => {
        cancelled = true;
        if (genericPreviewMeshRef.current) {
          removePreviewMesh(genericPreviewMeshRef.current);
          genericPreviewMeshRef.current = null;
        }
      };
    }

    // Нет GLB — используем примитив
    const previewMesh = createPrimitiveMesh(
      { ...pendingElement, id: '__preview_generic__' },
      material,
      categories
    );
    placePreview(previewMesh);

    return () => {
      if (genericPreviewMeshRef.current) {
        removePreviewMesh(genericPreviewMeshRef.current);
        genericPreviewMeshRef.current = null;
      }
    };
  }, [pendingElement, getLayerHeights, createPrimitiveMesh, sceneReady, categories]);

  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x87ceeb);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      50,
      containerRef.current.clientWidth / containerRef.current.clientHeight,
      0.1,
      1000
    );

    const isMobileView = window.innerWidth < 768;
    // Памятники: фронтальный ракурс — камера строго по оси Z перед стелой,
    // слегка выше центра, чтобы видеть лицевую сторону (гравировку/портрет)
    // без 3/4-перспективы. Орбита остаётся — пользователь сам может развернуть.
    if (isPamyatniki) {
      // В embed-режиме (iframe лендинга) камера ближе и целится в верх
      // стелы — компактный кадр под маленькую превью-область.
      // В standalone — обычный фронтальный план.
      const isEmbedView = new URLSearchParams(window.location.search).get('embed') === '1';
      camera.position.set(
        0,
        isEmbedView ? (isMobileView ? 0.95 : 0.85) : (isMobileView ? 1.0 : 0.9),
        isEmbedView ? (isMobileView ? 2.0 : 2.1) : (isMobileView ? 2.4 : 2.6)
      );
    } else {
      camera.position.set(
        isMobileView ? 2.2 : 2.75,
        isMobileView ? 4.4 : 2.75,
        isMobileView ? 2.2 : 2.75
      );
    }
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    // Без setPixelRatio Three.js рисует в DPR=1 — на мобильных с физическим DPR 2.5-3×
    // это даёт явное мыло (canvas апскейлится браузером). Кап в 2× держит баланс
    // «чёткая картинка» против «не выжечь GPU на дешёвых телефонах».
    // Гейтим под isPamyatniki: blago-вариант оставляем как было, чтобы не задеть
    // его поведение/производительность вне рамок текущей задачи.
    if (isPamyatniki) {
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    }
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = isPamyatniki ? 1 : 2;
    controls.maxDistance = 20;
    controls.maxPolarAngle = Math.PI / 2 - 0.1;
    if (isPamyatniki) {
      // В embed целимся в верх стелы (~0.8 м) — памятник в нижней половине
      // кадра, больше неба сверху. В standalone — в середину (~0.6).
      const isEmbedView = new URLSearchParams(window.location.search).get('embed') === '1';
      controls.target.set(0, isEmbedView ? 0.8 : 0.6, 0);
    }
    controlsRef.current = controls;

    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(10, 15, 10);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    directionalLight2.position.set(-5, 10, -5);
    scene.add(directionalLight2);

    // Ground (base) — текстура из глобального groundTexture
    const groundGeometry = new THREE.PlaneGeometry(20, 20);
    const groundMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.9,
      metalness: 0,
      map: groundTexture
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.01;
    ground.receiveShadow = true;
    ground.name = 'ground';
    scene.add(ground);

    // Drag plane
    const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    dragPlaneRef.current = dragPlane;

    // Register camera reset callback for PDF export - returns horizontal screenshot
    const captureForPDF = () => {
      return new Promise((resolve) => {
        // Get current perimeter size from store
        const state = useStore.getState();
        const w = state.perimeterWidth;
        const l = state.perimeterLength;
        
        // Save original size
        const originalWidth = renderer.domElement.width;
        const originalHeight = renderer.domElement.height;
        
        // Set fixed horizontal size for PDF (16:9 aspect ratio)
        const pdfWidth = 1280;
        const pdfHeight = 720;
        
        // Temporarily resize renderer
        renderer.setSize(pdfWidth, pdfHeight);
        camera.aspect = pdfWidth / pdfHeight;
        camera.updateProjectionMatrix();
        
        // Calculate optimal camera position based on perimeter
        const maxDimension = Math.max(w, l);
        const distance = maxDimension * 2.2;
        
        // Set camera to isometric-like view from corner
        camera.position.set(distance * 0.8, distance * 0.6, distance * 0.8);
        camera.lookAt(0, 0, 0);
        controls.target.set(0, 0, 0);
        controls.update();
        
        // Render and capture
        renderer.render(scene, camera);
        const screenshot = renderer.domElement.toDataURL('image/jpeg', 0.92);
        
        // Restore original size
        renderer.setSize(originalWidth, originalHeight);
        camera.aspect = originalWidth / originalHeight;
        camera.updateProjectionMatrix();
        renderer.render(scene, camera);
        
        resolve(screenshot);
      });
    };
    setResetCameraCallback(() => captureForPDF);

    // Mark scene as initialized - triggers syncElements via state change
    setSceneReady(true);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Pamyatniki embed: квиз-мокап на imperial-ritual.kz просит снимок памятника
    // для «Экспорт PDF». Снимаем в фиксированном портретном кадре 600×800 —
    // иначе на десктопе iframe близок к квадрату и в PDF памятник плющится в
    // широкую полосу. После снимка восстанавливаем исходный размер канваса.
    // Гейтим под isPamyatniki — blago-бандл остаётся байт-идентичным (DCE).
    let handleSnapshotRequest = null;
    if (isPamyatniki) {
      handleSnapshotRequest = (ev) => {
        if (!ev.data || ev.data.type !== 'iri:request-snapshot') return;
        if (typeof window === 'undefined' || window.parent === window) return;
        try {
          const origW = renderer.domElement.width;
          const origH = renderer.domElement.height;
          const origAspect = camera.aspect;
          const SNAP_W = 600, SNAP_H = 800;
          renderer.setSize(SNAP_W, SNAP_H, false);
          camera.aspect = SNAP_W / SNAP_H;
          camera.updateProjectionMatrix();
          renderer.render(scene, camera);
          const dataUrl = renderer.domElement.toDataURL('image/jpeg', 0.9);
          // Возвращаем исходный размер — иначе видимый канвас останется в
          // портретной пропорции до следующего ResizeObserver-события.
          renderer.setSize(origW, origH, false);
          camera.aspect = origAspect;
          camera.updateProjectionMatrix();
          renderer.render(scene, camera);
          window.parent.postMessage({ type: 'iri:snapshot', dataUrl, requestId: ev.data.requestId || null }, '*');
        } catch (e) {
          window.parent.postMessage({ type: 'iri:snapshot', dataUrl: null, error: String(e), requestId: ev.data.requestId || null }, '*');
        }
      };
      window.addEventListener('message', handleSnapshotRequest);
    }

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    };
    window.addEventListener('resize', handleResize);

    // Also observe container size changes (e.g. mobile layout transitions)
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerRef.current);

    // Get pointer position (works for both mouse and touch)
    const getPointerPosition = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      let clientX, clientY;
      
      if (event.touches && event.touches.length > 0) {
        // Touch event
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else if (event.changedTouches && event.changedTouches.length > 0) {
        // Touch end event
        clientX = event.changedTouches[0].clientX;
        clientY = event.changedTouches[0].clientY;
      } else {
        // Mouse event
        clientX = event.clientX;
        clientY = event.clientY;
      }
      
      mouseRef.current.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    };

    const findElementIntersection = () => {
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      
      // Только реально поставленные объекты (превью не участвует и не выделяется)
      const meshesToTest = [];
      Object.values(elementsRef.current).forEach(group => {
        if (group.userData.isPreview) return;
        group.traverse(child => {
          if (child.isMesh && child.userData.isPlacedElement && !child.userData.isPreview) {
            meshesToTest.push(child);
          }
        });
      });
      
      if (meshesToTest.length === 0) return null;
      
      const intersects = raycasterRef.current.intersectObjects(meshesToTest, false);
      
      if (intersects.length > 0) {
        let obj = intersects[0].object;
        while (obj) {
          if (obj.userData && obj.userData.isPreview) return null;
          if (obj.userData && obj.userData.elementId) {
            if (obj.userData.elementId === '__preview__') return null;
            return obj.userData.elementId;
          }
          obj = obj.parent;
        }
      }
      return null;
    };

    const handlePointerDown = (event) => {
      getPointerPosition(event);
      
      const elementId = findElementIntersection();
      
      if (elementId) {
        const currentId = useStore.getState().selectedPlacedId;
        
        if (elementId !== currentId) {
          // Select the element
          setSelectedPlacedId(elementId);
        } else {
          // Start dragging if already selected
          isDraggingRef.current = true;
          controls.enabled = false;
          
          // Prevent touch scrolling while dragging
          if (event.cancelable) {
            event.preventDefault();
          }
          
          raycasterRef.current.setFromCamera(mouseRef.current, camera);
          const planeIntersect = new THREE.Vector3();
          raycasterRef.current.ray.intersectPlane(dragPlaneRef.current, planeIntersect);
          
          const group = elementsRef.current[elementId];
          if (group) {
            offsetRef.current.copy(planeIntersect).sub(group.position);
          }
        }
      } else {
        setSelectedPlacedId(null);
      }
    };

    const handlePointerMove = (event) => {
      if (!isDraggingRef.current) return;
      
      const currentId = useStore.getState().selectedPlacedId;
      if (!currentId) return;

      // Prevent touch scrolling while dragging
      if (event.cancelable) {
        event.preventDefault();
      }

      getPointerPosition(event);
      raycasterRef.current.setFromCamera(mouseRef.current, camera);
      
      const planeIntersect = new THREE.Vector3();
      raycasterRef.current.ray.intersectPlane(dragPlaneRef.current, planeIntersect);
      
      const halfWidth = useStore.getState().perimeterWidth / 2;
      const halfLength = useStore.getState().perimeterLength / 2;
      const element = useStore.getState().placedElements.find(e => e.id === currentId);
      
      const group = elementsRef.current[currentId];
      if (element && group) {
        // Use world-space bounding box to get REAL dimensions regardless of rotation/scale
        // Temporarily move to origin to measure pure size
        const savedPos = group.position.clone();
        group.position.set(0, savedPos.y, 0);
        const box = new THREE.Box3().setFromObject(group);
        group.position.copy(savedPos);
        
        const boxSize = box.getSize(new THREE.Vector3());
        // boxSize.x and boxSize.z are ALREADY accounting for rotation since Box3 is axis-aligned
        const effectiveHalfWidth = boxSize.x / 2;
        const effectiveHalfDepth = boxSize.z / 2;
        
        let newX = Math.max(-halfWidth + effectiveHalfWidth, Math.min(halfWidth - effectiveHalfWidth, planeIntersect.x - offsetRef.current.x));
        let newZ = Math.max(-halfLength + effectiveHalfDepth, Math.min(halfLength - effectiveHalfDepth, planeIntersect.z - offsetRef.current.z));

        // Мягкое примагничивание к другим объектам (небольшое, при приближении)
        const SNAP_THRESHOLD = 0.12;
        const others = useStore.getState().placedElements.filter((e) => e.id !== currentId);
        let bestSnapX = newX, bestSnapZ = newZ;
        let bestDistX = SNAP_THRESHOLD, bestDistZ = SNAP_THRESHOLD;
        for (const other of others) {
          const otherGroup = elementsRef.current[other.id];
          if (!otherGroup) continue;
          otherGroup.updateMatrixWorld(true);
          const otherBox = new THREE.Box3().setFromObject(otherGroup);
          const oMinX = otherBox.min.x, oMaxX = otherBox.max.x, oCenX = (oMinX + oMaxX) * 0.5;
          const oMinZ = otherBox.min.z, oMaxZ = otherBox.max.z, oCenZ = (oMinZ + oMaxZ) * 0.5;
          const snapXCandidates = [oMaxX + effectiveHalfWidth, oMinX - effectiveHalfWidth, oCenX];
          const snapZCandidates = [oMaxZ + effectiveHalfDepth, oMinZ - effectiveHalfDepth, oCenZ];
          for (const tx of snapXCandidates) {
            const d = Math.abs(newX - tx);
            if (d < bestDistX) { bestDistX = d; bestSnapX = tx; }
          }
          for (const tz of snapZCandidates) {
            const d = Math.abs(newZ - tz);
            if (d < bestDistZ) { bestDistZ = d; bestSnapZ = tz; }
          }
        }
        newX = bestSnapX;
        newZ = bestSnapZ;
        // Повторная проверка границ после snap
        newX = Math.max(-halfWidth + effectiveHalfWidth, Math.min(halfWidth - effectiveHalfWidth, newX));
        newZ = Math.max(-halfLength + effectiveHalfDepth, Math.min(halfLength - effectiveHalfDepth, newZ));
        
        group.position.x = newX;
        group.position.z = newZ;
      }
    };

    const handlePointerUp = () => {
      if (isDraggingRef.current) {
        const currentId = useStore.getState().selectedPlacedId;
        if (currentId) {
          const group = elementsRef.current[currentId];
          if (group) {
            updatePlacedElement(currentId, {
              position: {
                x: group.position.x,
                y: 0, // Y is always computed in syncElements; store 0 as neutral
                z: group.position.z
              }
            });
          }
        }
        isDraggingRef.current = false;
        controls.enabled = true;
      }
    };

    // Mouse events
    renderer.domElement.addEventListener('mousedown', handlePointerDown);
    renderer.domElement.addEventListener('mousemove', handlePointerMove);
    renderer.domElement.addEventListener('mouseup', handlePointerUp);
    
    // Touch events for iPad/mobile
    renderer.domElement.addEventListener('touchstart', handlePointerDown, { passive: false });
    renderer.domElement.addEventListener('touchmove', handlePointerMove, { passive: false });
    renderer.domElement.addEventListener('touchend', handlePointerUp);

    const container = containerRef.current;

    return () => {
      window.removeEventListener('resize', handleResize);
      if (handleSnapshotRequest) window.removeEventListener('message', handleSnapshotRequest);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener('mousedown', handlePointerDown);
      renderer.domElement.removeEventListener('mousemove', handlePointerMove);
      renderer.domElement.removeEventListener('mouseup', handlePointerUp);
      renderer.domElement.removeEventListener('touchstart', handlePointerDown);
      renderer.domElement.removeEventListener('touchmove', handlePointerMove);
      renderer.domElement.removeEventListener('touchend', handlePointerUp);
      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [setSelectedPlacedId, updatePlacedElement]);

  // Sync elements when they change or when scene becomes ready
  useEffect(() => {
    if (sceneReady) {
      syncElements();
    }
  }, [syncElements, sceneReady]);

  // Шаг «Размер»: показываем силуэт-эталон 170 см слева от стелы и переводим
  // камеру в 3/4-ракурс, чтобы была видна глубина. На любом другом шаге —
  // возвращаем фронтальный pamyatniki-вид и убираем силуэт.
  useEffect(() => {
    if (!sceneReady || !isPamyatniki) return;
    const scene = sceneRef.current;
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!scene || !camera || !controls) return;

    const isMobileView = window.innerWidth < 768;

    if (quizViewMode === 'size') {
      if (!sizeRefHumanRef.current) {
        const human = buildSizeRefHuman();
        human.position.set(-1.0, 0, -0.7);
        sizeRefHumanRef.current = human;
        scene.add(human);

        // Подгружаем реалистичную фигуру (170 см, основание на y=0, Y-up, Draco,
        // ~0.47 МБ локально). Примитивная заглушка видна доли секунды до загрузки.
        // Если файла нет или загрузка упала — остаётся примитив (graceful fallback).
        const loader = gltfLoaderRef.current;
        if (loader) {
          loader.load(
            '/models/person_170cm_draco.glb',
            (gltf) => {
              // За время загрузки могли уйти с шага «Размер» (ref обнулён/заменён) —
              // тогда модель не нужна, освобождаем и выходим.
              const host = sizeRefHumanRef.current;
              if (!host || host !== human) { disposeSizeRefGroup(gltf.scene); return; }
              const model = gltf.scene;
              // Разворот проверен рендером под камерой шага «Размер»: дефолтная
              // ориентация (0) ставит фигуру лицом к камере 3/4-ракурса. Math.PI
              // развернул бы её спиной.
              model.rotation.y = 0;
              model.traverse((o) => {
                if (!o.isMesh) return;
                o.castShadow = true;
                o.receiveShadow = true;
                // Модель экспортирована с metalness=1 без metal/roughness- и env-карт,
                // из-за чего без environment в сцене рендерилась почти чёрной. Делаем
                // материал матовым (metalness=0) — albedo-текстура нормально освещается
                // штатным светом сцены. Проверено рендером под камерой шага «Размер».
                const mats = Array.isArray(o.material) ? o.material : [o.material];
                mats.forEach((m) => { if (m) { m.metalness = 0; m.needsUpdate = true; } });
              });
              host.add(model);
              // Убираем примитивную заглушку, подпись «170 см» остаётся.
              const ph = host.getObjectByName('__size-ref-placeholder__');
              if (ph) { host.remove(ph); disposeSizeRefGroup(ph); }
            },
            undefined,
            (err) => console.warn('[size-ref] GLB-фигура не загрузилась, оставляем примитив', err)
          );
        }
      }
      camera.position.set(
        isMobileView ? 1.6 : 1.9,
        isMobileView ? 1.3 : 1.4,
        isMobileView ? 1.7 : 2.0
      );
      controls.target.set(-0.4, 0.85, -0.7);
      controls.update();
    } else {
      if (sizeRefHumanRef.current) {
        scene.remove(sizeRefHumanRef.current);
        disposeSizeRefGroup(sizeRefHumanRef.current);
        sizeRefHumanRef.current = null;
      }
      const isEmbedView = new URLSearchParams(window.location.search).get('embed') === '1';
      // Семейная стела — широкая (1.2 м) и низкая (0.6 м), фронтальный план
      // с дефолтной камерой для вертикальной стелы её обрезает по краям.
      // В embed-режиме переключаемся на более близкий и низкий ракурс.
      const hasFamilyStele = placedElements.some((el) => el.name === 'family_stele');
      if (isPamyatniki && quizViewMode === 'flowerbed') {
        // Шаг «Цветник»: цветник лежит перед памятником на земле и при фронтальной
        // камере оказывается вплотную/обрезанным. Отъезжаем, приподнимаем камеру и
        // даём 3/4-ракурс, цель опускаем ниже и чуть вперёд — цветник виден целиком
        // и «под углом». Ветка под isPamyatniki → для blago сворачивается.
        camera.position.set(
          isMobileView ? 1.7 : 2.0,
          isMobileView ? 1.5 : 1.65,
          isMobileView ? 2.7 : 3.0
        );
        controls.target.set(0, 0.4, 0.3);
      } else if (isEmbedView && hasFamilyStele) {
        camera.position.set(0, isMobileView ? 0.6 : 0.55, isMobileView ? 1.7 : 1.8);
        controls.target.set(0, 0.45, 0);
      } else {
        camera.position.set(
          0,
          isEmbedView ? (isMobileView ? 0.95 : 0.85) : (isMobileView ? 1.0 : 0.9),
          isEmbedView ? (isMobileView ? 2.0 : 2.1) : (isMobileView ? 2.4 : 2.6)
        );
        controls.target.set(0, isEmbedView ? 0.8 : 0.6, 0);
      }
      controls.update();
    }
  }, [quizViewMode, sceneReady, placedElements]);

  // Update perimeter boundary (flat lines on ground level)
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;

    const oldBoundary = scene.getObjectByName('perimeter');
    if (oldBoundary) scene.remove(oldBoundary);

    // У памятников нет участка — рамку периметра не рисуем.
    if (isPamyatniki) return;

    const group = new THREE.Group();
    group.name = 'perimeter';
    
    // Flat boundary markers on ground
    const material = new THREE.MeshStandardMaterial({ color: 0x8b7355 });
    const thickness = 0.05;  // Width of the line
    const height = 0.005;    // Almost flat (5mm)

    const front = new THREE.Mesh(new THREE.BoxGeometry(perimeterWidth + thickness * 2, height, thickness), material);
    front.position.set(0, height / 2, -perimeterLength / 2 - thickness / 2);
    group.add(front);

    const back = new THREE.Mesh(new THREE.BoxGeometry(perimeterWidth + thickness * 2, height, thickness), material);
    back.position.set(0, height / 2, perimeterLength / 2 + thickness / 2);
    group.add(back);

    const left = new THREE.Mesh(new THREE.BoxGeometry(thickness, height, perimeterLength), material);
    left.position.set(-perimeterWidth / 2 - thickness / 2, height / 2, 0);
    group.add(left);

    const right = new THREE.Mesh(new THREE.BoxGeometry(thickness, height, perimeterLength), material);
    right.position.set(perimeterWidth / 2 + thickness / 2, height / 2, 0);
    group.add(right);

    scene.add(group);
  }, [perimeterWidth, perimeterLength]);

  // Update foundation (Layer 1)
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;

    const oldFoundation = scene.getObjectByName('foundation');
    if (oldFoundation) scene.remove(oldFoundation);

    if (selectedFoundation) {
      const geometry = new THREE.BoxGeometry(perimeterWidth, selectedFoundation.height, perimeterLength);
      // Если выбран фундамент "Земля" — показываем текстуру земли (а не просто коричневый цвет)
      const isEarth = selectedFoundation.name === 'earth' || selectedFoundation.name_ru === 'Земля';
      const tex = isEarth ? earthFoundationTexture.clone() : null;
      if (tex) {
        // Тайлинг по размеру участка: чтобы земля выглядела естественно и не "растягивалась"
        tex.repeat.set(Math.max(1, perimeterWidth / 1.0), Math.max(1, perimeterLength / 1.0));
        tex.needsUpdate = true;
      }
      const material = new THREE.MeshStandardMaterial({
        color: tex ? 0xffffff : (selectedFoundation.color || '#9ca3af'),
        roughness: 0.9,
        metalness: 0,
        map: tex
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.name = 'foundation';
      mesh.position.set(0, selectedFoundation.height / 2, 0);
      mesh.receiveShadow = true;
      scene.add(mesh);
    }
  }, [selectedFoundation, perimeterWidth, perimeterLength]);

  // Update floor (Layer 2)
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;
    const { foundationHeight } = getLayerHeights();

    const oldFloor = scene.getObjectByName('floor');
    if (oldFloor) scene.remove(oldFloor);
    const oldFloorGrid = scene.getObjectByName('floor-grid');
    if (oldFloorGrid) scene.remove(oldFloorGrid);

    if (selectedFloor) {
      const geometry = new THREE.BoxGeometry(perimeterWidth - 0.02, selectedFloor.height, perimeterLength - 0.02);
      const isGrass = selectedFloor.name === 'grass' || selectedFloor.name_ru === 'Трава';
      // Превью из админки часто лежит только в thumbnail_url — подставляем и его
      const floorTex = !isGrass
        ? getFloorTexture(selectedFloor.texture_url || selectedFloor.thumbnail_url, selectedFloor)
        : null;
      if (floorTex) {
        // тайлим по размеру участка, чтобы текстура выглядела естественно
        floorTex.wrapS = floorTex.wrapT = THREE.RepeatWrapping;
        const isMarbleFloor = selectedFloor.name === 'marble_chips' || (selectedFloor.name_ru || '').includes('Мраморная') || (selectedFloor.texture_url || selectedFloor.thumbnail_url || '').includes('mramor');
        const floorTileSize = isMarbleFloor ? 0.7 : 1.2;
        floorTex.repeat.set(Math.max(1, perimeterWidth / floorTileSize), Math.max(1, perimeterLength / floorTileSize));
      }
      if (isGrass) {
        grassTexture.repeat.set(Math.max(1, perimeterWidth / 1.5), Math.max(1, perimeterLength / 1.5));
      }
      const material = new THREE.MeshStandardMaterial({ 
        color: isGrass ? 0xffffff : (floorTex ? 0xffffff : (selectedFloor.color || '#4a7c59')),
        roughness: 0.7,
        map: isGrass ? grassTexture : (floorTex || null)
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.name = 'floor';
      mesh.position.set(0, foundationHeight + selectedFloor.height / 2, 0);
      mesh.receiveShadow = true;
      scene.add(mesh);

      // Визуальные «швы» плитки: сетка только для гранита/плитки, не для травы и не для сыпучих (крошка)
      const showTileSeams = !isGrass && !isLooseFloorMaterial(selectedFloor);
      if (showTileSeams) {
        const tileSize = 0.5; // 50 см плитка по умолчанию (меньше "шума" на картинке)
        const w = Math.max(0.1, perimeterWidth - 0.02);
        const l = Math.max(0.1, perimeterLength - 0.02);
        const halfW = w / 2;
        const halfL = l / 2;

        // Зоны, где НЕ рисуем швы (например под цветниками), чтобы сетка не заходила на элементы
        const noGridZones = (placedElements || [])
          .filter((el) => el && (el.model_type === 'flowerbed' || el.flowerbed_shape != null))
          .map((el) => {
            const cx = el.position?.x || 0;
            const cz = el.position?.z || 0;
            const ew = el.width || 0.5;
            const ed = el.depth || 0.5;
            const pad = 0.02; // небольшой запас, чтобы визуально не "подлезало" к краям
            return {
              minX: cx - ew / 2 - pad,
              maxX: cx + ew / 2 + pad,
              minZ: cz - ed / 2 - pad,
              maxZ: cz + ed / 2 + pad,
            };
          });

        const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
        const subtractIntervals = (base, cuts) => {
          // base: [start, end], cuts: array of [start, end]
          let segments = [base];
          (cuts || [])
            .filter(([a, b]) => b > a)
            .sort((p, q) => p[0] - q[0])
            .forEach(([cutA, cutB]) => {
              const next = [];
              segments.forEach(([s, e]) => {
                if (cutB <= s || cutA >= e) {
                  next.push([s, e]);
                  return;
                }
                if (cutA > s) next.push([s, cutA]);
                if (cutB < e) next.push([cutB, e]);
              });
              segments = next;
            });
          return segments;
        };

        const lines = [];
        // Линии вдоль Z (вертикальные, по X)
        for (let x = -halfW; x <= halfW + 1e-6; x += tileSize) {
          const cuts = noGridZones
            .filter((z) => x >= z.minX && x <= z.maxX)
            .map((z) => [
              clamp(z.minZ, -halfL, halfL),
              clamp(z.maxZ, -halfL, halfL),
            ]);
          const segments = subtractIntervals([-halfL, halfL], cuts);
          segments.forEach(([zs, ze]) => {
            lines.push(x, 0, zs, x, 0, ze);
          });
        }
        // Линии вдоль X (горизонтальные, по Z)
        for (let z = -halfL; z <= halfL + 1e-6; z += tileSize) {
          const cuts = noGridZones
            .filter((xZone) => z >= xZone.minZ && z <= xZone.maxZ)
            .map((xZone) => [
              clamp(xZone.minX, -halfW, halfW),
              clamp(xZone.maxX, -halfW, halfW),
            ]);
          const segments = subtractIntervals([-halfW, halfW], cuts);
          segments.forEach(([xs, xe]) => {
            lines.push(xs, 0, z, xe, 0, z);
          });
        }

        const gridGeom = new THREE.BufferGeometry();
        gridGeom.setAttribute('position', new THREE.Float32BufferAttribute(lines, 3));
        const gridMat = new THREE.LineBasicMaterial({
          color: 0x000000,
          transparent: true,
          // Чуть усилили швы, чтобы они были заметнее на полу
          opacity: 0.22,
          // Важно: линии должны быть видны ТОЛЬКО на полу.
          // Поэтому depthTest включён — стол/памятник и другие объекты будут перекрывать "швы".
          depthTest: true,
          depthWrite: false,
        });
        const grid = new THREE.LineSegments(gridGeom, gridMat);
        grid.name = 'floor-grid';
        // Рисуем чуть позже пола, но не "поверх всего"
        grid.renderOrder = 2;
        // чуть выше поверхности пола, чтобы не мерцало (z-fighting)
        grid.position.set(0, foundationHeight + selectedFloor.height + 0.008, 0);
        scene.add(grid);
      }
    }
  }, [selectedFloor, selectedFoundation, perimeterWidth, perimeterLength, placedElements, getLayerHeights]);

  // Update border (parapet around perimeter) - supports GLB models
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;
    const { foundationHeight, floorHeight } = getLayerHeights();

    // Remove old border
    const oldBorder = scene.getObjectByName('border');
    if (oldBorder) {
      oldBorder.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      scene.remove(oldBorder);
    }

    if (selectedBorder) {
      const group = new THREE.Group();
      group.name = 'border';
      
      // Border height: at least 10cm, but adapts to foundation/floor height
      const minHeight = 0.10;
      const layerHeight = foundationHeight + floorHeight;
      const borderHeight = Math.max(minHeight, layerHeight);
      const borderWidth = selectedBorder.width || 0.08;
      const sectionWidth = selectedBorder.section_width || 0.5;
      
      // Check if border has GLB model
      if (selectedBorder.model_url && (selectedBorder.model_url.endsWith('.glb') || selectedBorder.model_url.endsWith('.gltf'))) {
        setModelLoading('border', true);
        
        // Build full URL for model
        const backendBase = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const modelUrl = selectedBorder.model_url.startsWith('http')
          ? selectedBorder.model_url
          : `${backendBase}${selectedBorder.model_url}`;
        
        loadGLBViaBlob(
          gltfLoaderRef.current,
          modelUrl,
          (gltf) => {
            const originalModel = gltf.scene;

            const box = new THREE.Box3().setFromObject(originalModel);
            const size = box.getSize(new THREE.Vector3());
            const modelScale = sectionWidth / size.x;
            
            const placeSectionsAlongSide = (sideLength, startX, startZ, rotationY, offsetX, offsetZ) => {
              const numSections = Math.ceil(sideLength / sectionWidth);
              
              for (let i = 0; i < numSections; i++) {
                const sectionClone = originalModel.clone();
                sectionClone.scale.setScalar(modelScale);
                
                if (selectedBorder.color && selectedBorder.color.trim() !== '') {
                  sectionClone.traverse((child) => {
                    if (child.isMesh && child.material) {
                      if (Array.isArray(child.material)) {
                        child.material = child.material.map(mat => {
                          const newMat = mat.clone();
                          newMat.color = new THREE.Color(selectedBorder.color);
                          return newMat;
                        });
                      } else {
                        child.material = child.material.clone();
                        child.material.color = new THREE.Color(selectedBorder.color);
                      }
                    }
                  });
                }
                
                sectionClone.traverse((child) => {
                  if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                  }
                });
                
                const progress = (i + 0.5) / numSections;
                const posX = startX + (progress * sideLength - sideLength / 2) * Math.cos(rotationY) + offsetX;
                const posZ = startZ + (progress * sideLength - sideLength / 2) * Math.sin(rotationY) + offsetZ;
                
                sectionClone.position.set(posX, 0, posZ);
                sectionClone.rotation.y = rotationY;
                
                group.add(sectionClone);
              }
            };
            
            const borderOffset = borderWidth / 2;
            placeSectionsAlongSide(perimeterWidth, 0, -perimeterLength / 2 - borderOffset, 0, 0, 0);
            placeSectionsAlongSide(perimeterWidth, 0, perimeterLength / 2 + borderOffset, Math.PI, 0, 0);
            placeSectionsAlongSide(perimeterLength, -perimeterWidth / 2 - borderOffset, 0, -Math.PI / 2, 0, 0);
            placeSectionsAlongSide(perimeterLength, perimeterWidth / 2 + borderOffset, 0, Math.PI / 2, 0, 0);
            
            scene.add(group);
            clearModelLoading('border');
          },
          undefined,
          (error) => {
            console.error('Error loading border GLB model:', error);
            clearModelLoading('border');
            createPrimitiveBorder(group, selectedBorder, borderHeight, borderWidth);
            scene.add(group);
          }
        );
      } else {
        createPrimitiveBorder(group, selectedBorder, borderHeight, borderWidth);
        scene.add(group);
      }
    }
  }, [selectedBorder, selectedFoundation, selectedFloor, perimeterWidth, perimeterLength, getLayerHeights, setModelLoading, clearModelLoading]);

  // Helper function to create primitive border (fallback)
  const createPrimitiveBorder = useCallback((group, border, borderHeight, borderWidth) => {
    const tex = getFloorTexture(border?.texture_url, border);
    if (tex) {
      // Чтобы текстура не была растянутой "в один пиксель"
      tex.repeat.set(2, 1);
      tex.needsUpdate = true;
    }
    const material = new THREE.MeshStandardMaterial({
      color: border.color || '#888888',
      roughness: 0.7,
      metalness: 0.1,
      map: tex || null,
    });
    
    // Front
    const frontGeo = new THREE.BoxGeometry(perimeterWidth + borderWidth * 2, borderHeight, borderWidth);
    const front = new THREE.Mesh(frontGeo, material);
    front.position.set(0, borderHeight / 2, -perimeterLength / 2 - borderWidth / 2);
    front.castShadow = true;
    group.add(front);
    
    // Back
    const backGeo = new THREE.BoxGeometry(perimeterWidth + borderWidth * 2, borderHeight, borderWidth);
    const back = new THREE.Mesh(backGeo, material);
    back.position.set(0, borderHeight / 2, perimeterLength / 2 + borderWidth / 2);
    back.castShadow = true;
    group.add(back);
    
    // Left
    const leftGeo = new THREE.BoxGeometry(borderWidth, borderHeight, perimeterLength);
    const left = new THREE.Mesh(leftGeo, material);
    left.position.set(-perimeterWidth / 2 - borderWidth / 2, borderHeight / 2, 0);
    left.castShadow = true;
    group.add(left);
    
    // Right
    const rightGeo = new THREE.BoxGeometry(borderWidth, borderHeight, perimeterLength);
    const right = new THREE.Mesh(rightGeo, material);
    right.position.set(perimeterWidth / 2 + borderWidth / 2, borderHeight / 2, 0);
    right.castShadow = true;
    group.add(right);
  }, [perimeterWidth, perimeterLength]);

  // Update fence (around perimeter) - supports GLB models
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;
    const { foundationHeight, floorHeight } = getLayerHeights();
    const baseY = foundationHeight + floorHeight;

    // Remove old fence - remove ALL fences to prevent duplicates
    const oldFences = [];
    scene.traverse((child) => {
      if (child.name === 'fence') {
        oldFences.push(child);
      }
    });
    oldFences.forEach((oldFence) => {
      oldFence.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      scene.remove(oldFence);
    });

    if (selectedFence) {
      const group = new THREE.Group();
      group.name = 'fence';
      
      const fenceHeight = selectedFence.height || 0.8; // Target fence height
      
      // Check if fence has GLB model
      if (selectedFence.model_url && (selectedFence.model_url.endsWith('.glb') || selectedFence.model_url.endsWith('.gltf'))) {
        // Check if loader is ready
        if (!gltfLoaderRef.current) {
          console.warn('GLTFLoader not ready yet for fence');
          createPrimitiveFence(group, selectedFence, baseY);
          scene.add(group);
          return;
        }
        
        // Load GLB model as complete fence
        setModelLoading('fence', true);
        
        // Build full URL for model
        const backendBase = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const modelUrl = selectedFence.model_url.startsWith('http')
          ? selectedFence.model_url
          : `${backendBase}${selectedFence.model_url}`;
        
        console.log('Loading fence GLB from:', modelUrl);
        
        loadGLBViaBlob(
          gltfLoaderRef.current,
          modelUrl,
          (gltf) => {
            // Remove any fences that appeared during loading
            const currentFences = [];
            scene.traverse((child) => {
              if (child.name === 'fence') currentFences.push(child);
            });
            currentFences.forEach(f => scene.remove(f));
            
            const originalModel = gltf.scene;
            
            // Calculate model dimensions
            const box = new THREE.Box3().setFromObject(originalModel);
            const size = box.getSize(new THREE.Vector3());
            const center = box.getCenter(new THREE.Vector3());
            
            console.log('Fence GLB loaded. Size:', size.x, 'x', size.y, 'x', size.z);
            
            // Uniform scale by height so секция не растягивается по длине/толщине
            const uniformScale = fenceHeight / size.y;
            const scaledHeight = size.y * uniformScale;
            const segmentLength = size.x * uniformScale;
            const halfSegment = segmentLength / 2;
            // Небольший сдвиг оградки ВНУТРЬ участка, чтобы секции не выступали за кромку
            const fenceOffset = 0.03;

            // Helper: применить цвет и тени к одному сегменту
            const decorateSegment = (segment) => {
              if (selectedFence.color && selectedFence.color.trim() !== '') {
                segment.traverse((child) => {
                  if (child.isMesh && child.material) {
                    if (Array.isArray(child.material)) {
                      child.material = child.material.map(mat => {
                        const newMat = mat.clone();
                        newMat.color = new THREE.Color(selectedFence.color);
                        return newMat;
                      });
                    } else {
                      child.material = child.material.clone();
                      child.material.color = new THREE.Color(selectedFence.color);
                    }
                  }
                });
              }
              segment.traverse((child) => {
                if (child.isMesh) {
                  child.castShadow = true;
                  child.receiveShadow = true;
                }
              });
            };

            // Helper: создать один сегмент с нужным scale
            const createSegment = () => {
              const seg = originalModel.clone();
              seg.scale.set(uniformScale, uniformScale, uniformScale);
              decorateSegment(seg);
              return seg;
            };

            const baseYPos = baseY + scaledHeight / 2 - (center.y * uniformScale);
            const halfW = perimeterWidth / 2;
            const halfL = perimeterLength / 2;

            // Вариант 2:
            // - если помещается хотя бы одна целая секция: ставим floor(N) целых + одну растянутую на остаток
            // - если ни одна не помещается: одна секция, растянутая на всю длину стороны

            // Стороны по ширине (ось X), спереди и сзади
            const placeSideAlongWidth = (zPos) => {
              const sideLength = perimeterWidth;
              const fullCount = Math.floor(sideLength / segmentLength);
              const usedByFull = fullCount * segmentLength;
              const remainder = sideLength - usedByFull;
              const startX = -halfW;

              let offset = startX;

              // Полные секции
              for (let i = 0; i < fullCount; i++) {
                const seg = createSegment();
                const centerX = offset + halfSegment;
                seg.position.set(
                  centerX - center.x * uniformScale,
                  baseYPos,
                  zPos - center.z * uniformScale
                );
                group.add(seg);
                offset += segmentLength;
              }

              // Растянутая секция для остатка
              if (remainder > 0.001) {
                const seg = originalModel.clone();
                seg.scale.set(uniformScale, uniformScale, uniformScale);
                const stretch = remainder / segmentLength;
                seg.scale.x *= stretch;
                decorateSegment(seg);
                const centerX = offset + remainder / 2;
                seg.position.set(
                  centerX - center.x * seg.scale.x,
                  baseYPos,
                  zPos - center.z * uniformScale
                );
                group.add(seg);
              }

              // Если вообще не поместилась ни одна секция (очень маленький участок) — одна растянутая на всю сторону
              if (fullCount === 0 && remainder <= 0.001) {
                const seg = originalModel.clone();
                seg.scale.set(uniformScale, uniformScale, uniformScale);
                const stretch = sideLength / segmentLength;
                seg.scale.x *= stretch;
                decorateSegment(seg);
                const centerX = startX + sideLength / 2;
                seg.position.set(
                  centerX - center.x * seg.scale.x,
                  baseYPos,
                  zPos - center.z * uniformScale
                );
                group.add(seg);
              }
            };

            // Стороны по длине (ось Z), слева и справа
            const placeSideAlongLength = (xPos, rotationY) => {
              const sideLength = perimeterLength;
              const fullCount = Math.floor(sideLength / segmentLength);
              const usedByFull = fullCount * segmentLength;
              const remainder = sideLength - usedByFull;
              const startZ = -halfL;

              let offset = startZ;

              // Полные секции
              for (let i = 0; i < fullCount; i++) {
                const seg = createSegment();
                seg.rotation.y = rotationY;
                const centerZ = offset + halfSegment;
                seg.position.set(
                  xPos - center.z * uniformScale,
                  baseYPos,
                  centerZ - center.x * uniformScale
                );
                group.add(seg);
                offset += segmentLength;
              }

              // Растянутая секция для остатка
              if (remainder > 0.001) {
                const seg = originalModel.clone();
                seg.scale.set(uniformScale, uniformScale, uniformScale);
                const stretch = remainder / segmentLength;
                seg.scale.x *= stretch;
                decorateSegment(seg);
                seg.rotation.y = rotationY;
                const centerZ = offset + remainder / 2;
                seg.position.set(
                  xPos - center.z * uniformScale,
                  baseYPos,
                  centerZ - center.x * seg.scale.x
                );
                group.add(seg);
              }

              // Если вообще не поместилась ни одна секция
              if (fullCount === 0 && remainder <= 0.001) {
                const seg = originalModel.clone();
                seg.scale.set(uniformScale, uniformScale, uniformScale);
                const stretch = sideLength / segmentLength;
                seg.scale.x *= stretch;
                decorateSegment(seg);
                seg.rotation.y = rotationY;
                const centerZ = startZ + sideLength / 2;
                seg.position.set(
                  xPos - center.z * uniformScale,
                  baseYPos,
                  centerZ - center.x * seg.scale.x
                );
                group.add(seg);
              }
            };

            // Front (по ширине, спереди)
            placeSideAlongWidth(halfL - fenceOffset);

            // Back
            placeSideAlongWidth(-halfL + fenceOffset);

            // Left (по длине, слева)
            placeSideAlongLength(-halfW + fenceOffset, Math.PI / 2);

            // Right (по длине, справа)
            placeSideAlongLength(halfW - fenceOffset, Math.PI / 2);

            scene.add(group);
            clearModelLoading('fence');
          },
          undefined,
          (error) => {
            console.error('Error loading fence GLB model:', error);
            clearModelLoading('fence');
            // Fallback to primitive fence
            createPrimitiveFence(group, selectedFence, baseY);
            scene.add(group);
          }
        );
      } else {
        // Use primitive fence (no GLB model)
        createPrimitiveFence(group, selectedFence, baseY);
        scene.add(group);
      }
    }
  }, [selectedFence, selectedFoundation, selectedFloor, perimeterWidth, perimeterLength, getLayerHeights, setModelLoading, clearModelLoading]);

  // Helper function to create primitive fence (fallback)
  const createPrimitiveFence = useCallback((group, fence, baseY) => {
    const fenceOffset = 0.03;
    const material = new THREE.MeshStandardMaterial({ 
      color: fence.color || '#444444', 
      roughness: 0.6,
      metalness: 0.4
    });
    
    const fenceHeight = fence.height || 0.8;
    const barThickness = 0.02;
    const postThickness = 0.015;
    const postSpacing = 0.12;
    const bottomBarY = 0.05;
    const topBarY = fenceHeight - barThickness / 2;
    
    const createFenceSegment = (width, x, z, rotationY) => {
      const segmentGroup = new THREE.Group();
      
      // Top horizontal bar
      const topBarGeo = new THREE.BoxGeometry(width, barThickness, barThickness);
      const topBar = new THREE.Mesh(topBarGeo, material);
      topBar.position.set(0, topBarY, 0);
      topBar.castShadow = true;
      segmentGroup.add(topBar);
      
      // Bottom horizontal bar
      const bottomBarGeo = new THREE.BoxGeometry(width, barThickness, barThickness);
      const bottomBar = new THREE.Mesh(bottomBarGeo, material);
      bottomBar.position.set(0, bottomBarY, 0);
      bottomBar.castShadow = true;
      segmentGroup.add(bottomBar);
      
      // Middle horizontal bar
      const middleBarGeo = new THREE.BoxGeometry(width, barThickness, barThickness);
      const middleBar = new THREE.Mesh(middleBarGeo, material);
      middleBar.position.set(0, fenceHeight / 2, 0);
      middleBar.castShadow = true;
      segmentGroup.add(middleBar);
      
      // Vertical bars
      const verticalHeight = topBarY - bottomBarY - barThickness;
      const numPosts = Math.ceil(width / postSpacing) + 1;
      const actualSpacing = width / (numPosts - 1);
      
      for (let i = 0; i < numPosts; i++) {
        const postGeo = new THREE.BoxGeometry(postThickness, verticalHeight, postThickness);
        const post = new THREE.Mesh(postGeo, material);
        post.position.set(-width / 2 + i * actualSpacing, bottomBarY + barThickness / 2 + verticalHeight / 2, 0);
        post.castShadow = true;
        segmentGroup.add(post);
      }
      
      segmentGroup.position.set(x, baseY, z);
      segmentGroup.rotation.y = rotationY;
      return segmentGroup;
    };
    
    // Four sides of the fence — сдвигаем внутрь периметра на fenceOffset
    group.add(createFenceSegment(perimeterWidth, 0, -perimeterLength / 2 + fenceOffset, 0));
    group.add(createFenceSegment(perimeterWidth, 0, perimeterLength / 2 - fenceOffset, 0));
    group.add(createFenceSegment(perimeterLength, -perimeterWidth / 2 + fenceOffset, 0, Math.PI / 2));
    group.add(createFenceSegment(perimeterLength, perimeterWidth / 2 - fenceOffset, 0, Math.PI / 2));
  }, [perimeterWidth, perimeterLength]);

  return (
    <div 
      ref={containerRef} 
      className="absolute inset-0" 
      data-testid="3d-scene"
      style={{ cursor: 'grab' }}
    >
      {/* Initial texture loading overlay */}
      {!texturesReady && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
          <Loader2 className="h-10 w-10 text-primary animate-spin mb-3" />
          <span className="text-sm font-medium text-slate-700">Загрузка текстур...</span>
        </div>
      )}
      {/* Loading indicator for GLB models */}
      {isLoading && (
        <div 
          className="absolute top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
          data-testid="model-loading-indicator"
        >
          <div className="bg-white/95 backdrop-blur-sm shadow-lg rounded-xl px-4 py-3 flex items-center gap-3 border border-slate-200">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-slate-800">
                Загрузка модели...
              </span>
              <span className="text-xs text-slate-500">
                {loadingCount > 1 ? `${loadingCount} объектов` : 'Подождите немного'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
